import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import {
  Home,
  Calendar,
  MapPin,
  Sparkles,
  Bath,
  Bed,
  Lightbulb,
  MessageSquare,
  Clock,
} from "lucide-react";

// Cache the Stripe.js instance per (publishableKey, connectedAccountId)
// pair — React re-renders can call loadStripe many times, and re-loading
// the SDK on every call breaks the connected-account scoping for embedded
// Checkout. Same pattern the FlowSTR portal uses.
const stripeCache = new Map();
function getStripe(publishableKey, connectedAccountId) {
  const key = `${publishableKey}::${connectedAccountId}`;
  let p = stripeCache.get(key);
  if (!p) {
    p = loadStripe(publishableKey, connectedAccountId ? { stripeAccount: connectedAccountId } : undefined);
    stripeCache.set(key, p);
  }
  return p;
}
// ===== CONFIGURATION =====
// Google Places API Key - for address autocomplete
const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY || "";
const CLEANSYNC_WEBHOOK_URL = "https://cleansync-beryl.vercel.app/api/quotes";

// 8 AM through 5 PM at 30-min increments. Used by the From/To pickers in
// "Preferred Service Times" and the Instant-Book start-time picker.
const SERVICE_TIMES = [
  "8:00 AM","8:30 AM","9:00 AM","9:30 AM","10:00 AM","10:30 AM",
  "11:00 AM","11:30 AM","12:00 PM","12:30 PM","1:00 PM","1:30 PM",
  "2:00 PM","2:30 PM","3:00 PM","3:30 PM","4:00 PM","4:30 PM","5:00 PM",
];

// Custom calendar — replaces the OS-native <input type="date"> popover,
// which is unstyleable and renders inconsistently across browsers (and
// looks plain bad against the form's dark-glass aesthetic). Outputs the
// same YYYY-MM-DD string the native input would, so all downstream
// state/code keeps working.
function DatePicker({ value, onChange, minDate, placeholder = "Select a date", accent = "rgba(255,255,255,0.2)" }) {
  const [open, setOpen] = useState(false);
  // Month being viewed. Defaults to the selected date's month, else
  // today's month. Keep as Date pointing at the 1st of the month.
  const initMonth = (() => {
    if (value) {
      const [y, m] = value.split("-").map(Number);
      return new Date(y, m - 1, 1);
    }
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  })();
  const [viewMonth, setViewMonth] = useState(initMonth);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    function onEsc(e) { if (e.key === "Escape") setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // Re-sync viewMonth when value changes externally (rare, but defensive).
  useEffect(() => {
    if (value) {
      const [y, m] = value.split("-").map(Number);
      setViewMonth(new Date(y, m - 1, 1));
    }
  }, [value]);

  function fmt(d) {
    return d.toLocaleDateString("en-US", {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
  }
  function ymdToDate(ymd) {
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  function dateToYmd(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const minDateObj = minDate ? ymdToDate(minDate) : null;
  if (minDateObj) minDateObj.setHours(0, 0, 0, 0);
  const selectedDate = value ? ymdToDate(value) : null;

  // Build a 6-row x 7-col grid covering viewMonth (with leading/trailing
  // days from neighboring months greyed out).
  const firstOfMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const startDow = firstOfMonth.getDay(); // 0 = Sun
  const gridStart = new Date(firstOfMonth);
  gridStart.setDate(gridStart.getDate() - startDow);

  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }

  const monthLabel = viewMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const triggerDisplay = value ? fmt(ymdToDate(value)) : placeholder;
  const triggerActive = !!value;

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%",
          padding: "16px 16px 16px 44px",
          borderRadius: "14px",
          border: `2px solid ${open ? "#06b6d4" : (triggerActive ? "rgba(6,182,212,0.55)" : accent)}`,
          background: "rgba(255,255,255,0.95)",
          color: triggerActive ? "#0c4a6e" : "rgba(12,74,110,0.5)",
          fontSize: "16px",
          fontWeight: "600",
          outline: "none",
          boxSizing: "border-box",
          cursor: "pointer",
          textAlign: "left",
          position: "relative",
          boxShadow: open ? "0 0 0 4px rgba(6,182,212,0.18), 0 8px 24px rgba(6,182,212,0.22)" : "none",
          transition: "border-color .15s, box-shadow .15s",
          fontFamily: "inherit",
        }}
      >
        <span style={{
          position: "absolute",
          left: "14px",
          top: "50%",
          transform: "translateY(-50%)",
          fontSize: "16px",
          color: "#06b6d4",
        }}>📅</span>
        {triggerDisplay}
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            right: 0,
            minWidth: "300px",
            background: "linear-gradient(180deg, rgba(12,30,55,0.99) 0%, rgba(8,20,40,0.99) 100%)",
            border: "1.5px solid rgba(6,182,212,0.4)",
            borderRadius: "16px",
            boxShadow: "0 20px 50px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.05) inset",
            zIndex: 60,
            padding: "16px",
            backdropFilter: "blur(20px)",
            animation: "csuDpIn .15s ease-out",
          }}
        >
          <style>{`
            @keyframes csuDpIn {
              from { opacity: 0; transform: translateY(-6px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            .csu-dp-day:hover:not(.csu-dp-disabled):not(.csu-dp-selected) {
              background: rgba(6,182,212,0.18) !important;
              color: #e0f7fa !important;
            }
            .csu-dp-nav:hover {
              background: rgba(6,182,212,0.18) !important;
              color: #67e8f9 !important;
            }
          `}</style>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
            <button
              type="button"
              className="csu-dp-nav"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
              style={{ width: "34px", height: "34px", borderRadius: "10px", border: "1px solid rgba(6,182,212,0.25)", background: "rgba(6,182,212,0.06)", color: "#7dd3fc", fontSize: "16px", fontWeight: "900", cursor: "pointer", transition: "background .12s, color .12s" }}
            >‹</button>
            <div style={{ fontSize: "15px", fontWeight: "800", color: "#e0f7fa", letterSpacing: "0.3px" }}>{monthLabel}</div>
            <button
              type="button"
              className="csu-dp-nav"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
              style={{ width: "34px", height: "34px", borderRadius: "10px", border: "1px solid rgba(6,182,212,0.25)", background: "rgba(6,182,212,0.06)", color: "#7dd3fc", fontSize: "16px", fontWeight: "900", cursor: "pointer", transition: "background .12s, color .12s" }}
            >›</button>
          </div>
          {/* Day-of-week labels */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px", marginBottom: "6px" }}>
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
              <div key={i} style={{ fontSize: "11px", fontWeight: "700", color: "rgba(125,211,252,0.7)", textAlign: "center", letterSpacing: "0.5px" }}>{d}</div>
            ))}
          </div>
          {/* Date grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px" }}>
            {cells.map((d, i) => {
              const isOtherMonth = d.getMonth() !== viewMonth.getMonth();
              const isToday = d.getTime() === today.getTime();
              const isSelected = selectedDate && d.getTime() === selectedDate.getTime();
              const isDisabled = minDateObj ? d < minDateObj : false;
              const classes = ["csu-dp-day"];
              if (isDisabled) classes.push("csu-dp-disabled");
              if (isSelected) classes.push("csu-dp-selected");
              return (
                <button
                  key={i}
                  type="button"
                  className={classes.join(" ")}
                  disabled={isDisabled}
                  onClick={() => {
                    if (isDisabled) return;
                    onChange(dateToYmd(d));
                    setOpen(false);
                  }}
                  style={{
                    height: "36px",
                    borderRadius: "9px",
                    border: isToday && !isSelected ? "1.5px solid rgba(125,211,252,0.5)" : "1.5px solid transparent",
                    background: isSelected
                      ? "linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)"
                      : "transparent",
                    color: isDisabled
                      ? "rgba(180,200,220,0.2)"
                      : isOtherMonth
                        ? "rgba(180,200,220,0.35)"
                        : isSelected
                          ? "#ffffff"
                          : "rgba(220,240,250,0.88)",
                    fontSize: "13px",
                    fontWeight: isSelected || isToday ? "800" : "600",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                    boxShadow: isSelected ? "0 4px 12px rgba(6,182,212,0.45)" : "none",
                    transition: "background .12s, color .12s, transform .1s",
                    fontFamily: "inherit",
                  }}
                >{d.getDate()}</button>
              );
            })}
          </div>
          {/* Quick actions */}
          <div style={{ marginTop: "12px", paddingTop: "10px", borderTop: "1px solid rgba(6,182,212,0.15)", display: "flex", gap: "6px", justifyContent: "space-between", alignItems: "center" }}>
            <button
              type="button"
              onClick={() => {
                const t = new Date(); t.setHours(0,0,0,0);
                if (!minDateObj || t >= minDateObj) {
                  setViewMonth(new Date(t.getFullYear(), t.getMonth(), 1));
                  onChange(dateToYmd(t));
                  setOpen(false);
                }
              }}
              style={{ padding: "7px 14px", borderRadius: "8px", border: "1px solid rgba(6,182,212,0.3)", background: "transparent", color: "#7dd3fc", fontSize: "12px", fontWeight: "700", cursor: "pointer", fontFamily: "inherit" }}
            >Today</button>
            {value && (
              <button
                type="button"
                onClick={() => { onChange(""); setOpen(false); }}
                style={{ padding: "7px 14px", borderRadius: "8px", border: "1px solid rgba(255,255,255,0.12)", background: "transparent", color: "rgba(220,240,250,0.6)", fontSize: "12px", fontWeight: "600", cursor: "pointer", fontFamily: "inherit" }}
              >Clear</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Custom dropdown — replaces the OS-native <select> popover, which can't
// be styled and looks janky against the form's glassy aesthetic. The
// trigger keeps the same white-pill shape; the panel that opens is a
// dark glass card with hover-highlighted rows. Closes on outside click
// and on Escape.
function Dropdown({ value, onChange, options, placeholder, accent = "rgba(255,255,255,0.2)" }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    }
    function onEsc(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // Scroll active row into view when the panel opens
  useEffect(() => {
    if (open && listRef.current) {
      const active = listRef.current.querySelector("[data-active='true']");
      if (active) active.scrollIntoView({ block: "nearest" });
    }
  }, [open]);

  // Show the option's label in the trigger — using `value` directly broke
  // for cases like Frequency where the value is a slug ('every-week') and
  // the label is the human-friendly text ('Every Week (20% discount)').
  const selected = options.find(o => o.value === value);
  const display = selected ? selected.label : (placeholder || "");

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%",
          padding: "14px 44px 14px 16px",
          borderRadius: "12px",
          border: `2px solid ${open ? "#06b6d4" : accent}`,
          background: "rgba(255,255,255,0.95)",
          color: selected ? "#0c4a6e" : "rgba(12,74,110,0.5)",
          fontSize: "16px",
          fontWeight: "600",
          outline: "none",
          boxSizing: "border-box",
          cursor: "pointer",
          textAlign: "left",
          position: "relative",
          boxShadow: open ? "0 0 0 4px rgba(6,182,212,0.15), 0 8px 24px rgba(6,182,212,0.18)" : "none",
          transition: "border-color .15s, box-shadow .15s",
          fontFamily: "inherit",
        }}
      >
        {display}
        <span
          style={{
            position: "absolute",
            right: "16px",
            top: "50%",
            transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
            transition: "transform .2s ease",
            color: "#06b6d4",
            fontSize: "12px",
            fontWeight: "900",
            pointerEvents: "none",
          }}
        >▼</span>
      </button>
      {open && (
        <div
          ref={listRef}
          className="csu-dd-panel"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            right: 0,
            maxHeight: "260px",
            overflowY: "auto",
            background: "linear-gradient(180deg, rgba(12,30,55,0.98) 0%, rgba(8,20,40,0.98) 100%)",
            border: "1.5px solid rgba(6,182,212,0.4)",
            borderRadius: "14px",
            boxShadow: "0 16px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.05) inset",
            zIndex: 50,
            padding: "6px",
            backdropFilter: "blur(20px)",
            animation: "csuDropdownIn .14s ease-out",
          }}
        >
          <style>{`
            @keyframes csuDropdownIn {
              from { opacity: 0; transform: translateY(-6px); }
              to   { opacity: 1; transform: translateY(0); }
            }
            .csu-opt:hover { background: rgba(6,182,212,0.18) !important; }
            /* Custom scrollbar — the default OS scrollbar broke the glassy
               aesthetic. Teal track + gradient thumb on WebKit, with a
               Firefox fallback via scrollbar-color. */
            .csu-dd-panel {
              scrollbar-width: thin;
              scrollbar-color: rgba(6,182,212,0.55) rgba(255,255,255,0.04);
            }
            .csu-dd-panel::-webkit-scrollbar { width: 8px; }
            .csu-dd-panel::-webkit-scrollbar-track {
              background: rgba(255,255,255,0.04);
              border-radius: 8px;
              margin: 6px 0;
            }
            .csu-dd-panel::-webkit-scrollbar-thumb {
              background: linear-gradient(180deg, #06b6d4 0%, #0284c7 100%);
              border-radius: 8px;
              border: 1.5px solid rgba(12,30,55,0.6);
            }
            .csu-dd-panel::-webkit-scrollbar-thumb:hover {
              background: linear-gradient(180deg, #22d3ee 0%, #0ea5e9 100%);
            }
          `}</style>
          {options.map(opt => {
            const isActive = opt.value === value;
            return (
              <div
                key={opt.value}
                className="csu-opt"
                data-active={isActive}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                style={{
                  padding: "11px 14px",
                  borderRadius: "9px",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: isActive ? "700" : "600",
                  color: isActive ? "#06b6d4" : "rgba(220,235,250,0.9)",
                  background: isActive ? "rgba(6,182,212,0.14)" : "transparent",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "background .12s",
                }}
              >
                {isActive && <span style={{ fontSize: "12px" }}>✓</span>}
                <span>{opt.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
export default function App() {
  const formTopRef = useRef(null);
  const addressInputRef = useRef(null);
  // Form state
  const [step, setStep] = useState(1);

  // Load EmailJS SDK
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js';
    script.onload = () => window.emailjs.init('ZsAm6x2gjm0hFV69o');
    document.head.appendChild(script);
  }, []);
  const [mobileBarHeight, setMobileBarHeight] = useState(0);
  const mobileBarRef = useRef(null);
  useEffect(() => {
    const el = mobileBarRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setMobileBarHeight(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Synchronous reentrancy guard for handleSubmit. setIsSubmitting() is a
  // React state update so the button's disabled prop doesn't apply until
  // the next render — a double-tap (common on mobile) fires handleSubmit
  // twice before that, each POSTing to /api/quotes and creating a
  // DUPLICATE quote. A ref flips synchronously so the second call bails
  // immediately. (This was the root cause of the two-quotes-per-booking bug.)
  const submitLockRef = useRef(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [frequency, setFrequency] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  // House Cleaning state
  const [squareFeetRange, setSquareFeetRange] = useState("");
  const [bedrooms, setBedrooms] = useState(0);
  const [bathrooms, setBathrooms] = useState(0);
  // Airbnb Cleaning state
  const [airbnbSquareFeet, setAirbnbSquareFeet] = useState("");
  const [airbnbLaundry, setAirbnbLaundry] = useState("");
  const [airbnbBeds, setAirbnbBeds] = useState(0);
  const [airbnbUnits, setAirbnbUnits] = useState("");
  const [addOns, setAddOns] = useState({
      fridge: false,
      oven: false,
      microwave: false,
      deepClean: 0,
      linens: 0,
      dishes: false,
      windows: 0,
      pets: 0,
      baseTrimFeet: 0,
    });
const [keyAreas, setKeyAreas] = useState("");
const [additionalNotes, setAdditionalNotes] = useState("");
const [preferredDay1, setPreferredDay1] = useState("");
const [preferredDay2, setPreferredDay2] = useState("");
const [timeFrom, setTimeFrom] = useState("8:00 AM");
const [timeTo, setTimeTo] = useState("5:00 PM");
const [timeWindows, setTimeWindows] = useState([]);
const [submissionType, setSubmissionType] = useState(""); // 'quote' | 'instant_book'
const [isSubmitting, setIsSubmitting] = useState(false);
const [instantBookDate, setInstantBookDate] = useState("");
const [instantBookTime, setInstantBookTime] = useState("9:00 AM");
const [showInstantBookPicker, setShowInstantBookPicker] = useState(false);

// Embedded-checkout modal state. Three-phase lifecycle:
//   1. show=true + clientSecret=null → tip-selector view, "Continue to payment" button
//   2. show=true + clientSecret set  → EmbeddedCheckout iframe is mounted
//   3. paymentSuccess=true           → "Booking confirmed" success view
const [payModalOpen, setPayModalOpen] = useState(false);
const [pendingQuoteId, setPendingQuoteId] = useState(null);
const [tipMode, setTipMode] = useState("none"); // 'none' | '15' | '18' | '20' | '25' | 'custom'
const [tipCustom, setTipCustom] = useState("");
const [embeddedPay, setEmbeddedPay] = useState(null); // { clientSecret, publishableKey, connectedAccountId, totalCharge }
const [creatingPaySession, setCreatingPaySession] = useState(false);
const [paymentSuccess, setPaymentSuccess] = useState(false);
const [payError, setPayError] = useState("");
const [portalUrl, setPortalUrl] = useState("");

// Instant Book: 10% off first 5 cleanings — calculated inline where needed
// Load Google Places API and initialize autocomplete
useEffect(() => {
    // Only initialize when on step 2 and input is available
    if (step !== 2 || !addressInputRef.current) return;
    console.log("Initializing Google Places autocomplete...");
    const initializeAutocomplete = () => {
      if (!addressInputRef.current || !window.google || !window.google.maps) {
        console.log("Cannot initialize: missing requirements", {
            hasInput: !!addressInputRef.current,
            hasGoogle: !!window.google,
            hasMaps: !!(window.google && window.google.maps),
          });
      return;
    }
  console.log("Creating autocomplete instance...");
  const autocomplete = new window.google.maps.places.Autocomplete(
    addressInputRef.current,
    {
      types: ["address"],
      componentRestrictions: { country: "us" },
    }
);
autocomplete.addListener("place_changed", () => {
    const place = autocomplete.getPlace();
    console.log("Place selected:", place);
    if (place.address_components) {
      let streetNumber = "";
      let route = "";
      let cityName = "";
      let stateName = "";
      let zipCode = "";
      // Parse address components
      place.address_components.forEach((component) => {
          const types = component.types;
          if (types.includes("street_number")) {
            streetNumber = component.long_name;
          }
        if (types.includes("route")) {
          route = component.long_name;
        }
      if (types.includes("locality")) {
        cityName = component.long_name;
      }
    if (types.includes("administrative_area_level_1")) {
      stateName = component.short_name;
    }
  if (types.includes("postal_code")) {
    zipCode = component.long_name;
  }
});
// Set the split address fields
setAddress(`${streetNumber} ${route}`.trim());
setCity(cityName);
setState(stateName);
setZip(zipCode);
} else if (place.formatted_address) {
// Fallback to formatted address
setAddress(place.formatted_address);
}
});
console.log("Autocomplete initialized successfully!");
};
// Load Google Places script if not already loaded
if (window.google && window.google.maps && window.google.maps.places) {
  console.log("Google Maps already loaded, initializing...");
  initializeAutocomplete();
} else {
console.log("Loading Google Maps script...");
const script = document.createElement("script");
script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places`;
script.async = true;
script.defer = true;
script.onload = () => {
  console.log("Google Maps script loaded!");
  initializeAutocomplete();
};
script.onerror = () => {
  console.error("Failed to load Google Maps script");
};
// Check if script already exists
const existingScript = document.querySelector(
  `script[src*="maps.googleapis.com"]`
);
if (!existingScript) {
  document.head.appendChild(script);
} else {
console.log("Script already exists, waiting for load...");
existingScript.onload = () => initializeAutocomplete();
}
}
}, [step]); // Re-run when step changes
// Pricing constants

// Anchor points: median sqft of each bracket → price from original table
// Rate interpolates smoothly between anchors so every sqft step is slightly cheaper
const SQFT_ANCHORS = [
  { sqft: 499.5,  price: 71  },
  { sqft: 1249.5, price: 89  },
  { sqft: 1749.5, price: 124 },
  { sqft: 2249.5, price: 144 },
  { sqft: 2749.5, price: 164 },
  { sqft: 3249.5, price: 189 },
  { sqft: 3749.5, price: 214 },
  { sqft: 4249.5, price: 238 },
  { sqft: 4749.5, price: 259 },
  { sqft: 5249.5, price: 277 },
  { sqft: 5749.5, price: 300 },
  { sqft: 6249.5, price: 323 },
].map(a => ({ ...a, rate: a.price / a.sqft }));

const calcHouseSqftPrice = (sqft) => {
  if (!sqft || sqft <= 0) return 0;
  if (sqft <= SQFT_ANCHORS[0].sqft) return sqft * SQFT_ANCHORS[0].rate;
  if (sqft >= SQFT_ANCHORS[SQFT_ANCHORS.length-1].sqft) return sqft * SQFT_ANCHORS[SQFT_ANCHORS.length-1].rate;
  for (let i = 0; i < SQFT_ANCHORS.length - 1; i++) {
    const lo = SQFT_ANCHORS[i], hi = SQFT_ANCHORS[i+1];
    if (sqft >= lo.sqft && sqft <= hi.sqft) {
      const t = (sqft - lo.sqft) / (hi.sqft - lo.sqft);
      const rate = lo.rate + t * (hi.rate - lo.rate);
      return sqft * rate;
    }
  }
  return 0;
};

const frequencyDiscounts = {
  "every-week": 0.2,
  "bi-weekly": 0.15,
  "every-3-weeks": 0.12,
  "every-4-weeks": 0.09,
  "one-time": 0,
};
// Calculate total price
const calculateSubtotal = () => {
  let total = 0;
  if (serviceType === "Airbnb Cleaning") {
    // Airbnb pricing
    if (airbnbSquareFeet && airbnbLaundry) {
      const pricePerSqFt = airbnbLaundry === "yes" ? 0.095 : 0.09;
      total += parseFloat(airbnbSquareFeet) * pricePerSqFt;
    }
  total += airbnbBeds * 18;
  const fullBaths = Math.floor(bathrooms);
  const hasHalfBath = bathrooms % 1 !== 0;
  total += fullBaths * 20;
  if (hasHalfBath) total += 13;
  // Only certain add-ons for Airbnb
  total += addOns.windows * 6;
  total += addOns.deepClean * 65;
  total += addOns.pets * 10;
  total += addOns.baseTrimFeet * 0.54;
} else {
// House Cleaning pricing — smooth interpolated rate
if (squareFeetRange) {
  total += calcHouseSqftPrice(parseInt(squareFeetRange));
}
total += bedrooms * 16;
const fullBaths = Math.floor(bathrooms);
const hasHalfBath = bathrooms % 1 !== 0;
total += fullBaths * 20;
if (hasHalfBath) total += 13;
if (addOns.fridge) total += 35;
if (addOns.oven) total += 50;
if (addOns.microwave) total += 10;
total += addOns.deepClean * 65;
total += addOns.linens * 10;
if (addOns.dishes) total += 20;
total += addOns.windows * 6;
total += addOns.pets * 10;
total += addOns.baseTrimFeet * 0.54;
}
return total;
};
const getDiscount = () => {
  if (serviceType === "Airbnb Cleaning") {
    const subtotal = calculateSubtotal();
    let discountPercent = 0;
    // Frequency discount
    if (frequency === "1-3") discountPercent += 0;
    else if (frequency === "4-6") discountPercent += 0.03;
    else if (frequency === "7-9") discountPercent += 0.06;
    else if (frequency === "10+") discountPercent += 0.075;
    // Units discount (added to frequency discount)
    if (airbnbUnits === "2") discountPercent += 0.03;
    else if (airbnbUnits === "3") discountPercent += 0.05;
    else if (airbnbUnits === "4+") discountPercent += 0.07;
    return subtotal * discountPercent;
  }
// House Cleaning discount
const subtotal = calculateSubtotal();
if (frequency && frequencyDiscounts[frequency] !== undefined) {
  return subtotal * frequencyDiscounts[frequency];
}
return 0;
};
const calculateTotal = () => {
  const total = calculateSubtotal() - getDiscount();
  // Minimum: $139.99 for Airbnb with no laundry, $154.99 for everything else
  const minimum =
  serviceType === "Airbnb Cleaning" && airbnbLaundry === "no"
  ? 139.99
  : 154.99;
  return Math.max(minimum, total);
};
// Get price breakdown for sidebar
const getPriceBreakdown = () => {
  const items = [];
  if (serviceType === "Airbnb Cleaning") {
    if (airbnbSquareFeet && airbnbLaundry) {
      const pricePerSqFt = airbnbLaundry === "yes" ? 0.095 : 0.09;
      const sqFtCost = parseFloat(airbnbSquareFeet) * pricePerSqFt;
      items.push({
          label: `${airbnbSquareFeet} sq ft ${
            airbnbLaundry === "yes" ? "(w/ laundry)" : "(no laundry)"
          }`,
        amount: sqFtCost,
      });
}
if (airbnbBeds > 0) {
  items.push({
      label: `${airbnbBeds} Bed${airbnbBeds > 1 ? "s" : ""}`,
      amount: airbnbBeds * 18,
    });
}
if (bathrooms > 0) {
  const fullBaths = Math.floor(bathrooms);
  const hasHalfBath = bathrooms % 1 !== 0;
  let bathCost = fullBaths * 20;
  if (hasHalfBath) bathCost += 13;
  items.push({
      label: `${bathrooms} Bathroom${bathrooms > 1 ? "s" : ""}`,
      amount: bathCost,
    });
}
if (addOns.windows > 0)
items.push({
    label: `${addOns.windows} Window${addOns.windows > 1 ? "s" : ""}`,
    amount: addOns.windows * 6,
  });
if (addOns.deepClean > 0)
items.push({
    label: `Deep Clean +${addOns.deepClean}hr`,
    amount: addOns.deepClean * 65,
  });
if (addOns.pets > 0)
items.push({
    label: `${addOns.pets} Pet${addOns.pets > 1 ? "s" : ""}`,
    amount: addOns.pets * 10,
  });
if (addOns.baseTrimFeet > 0)
items.push({
    label: `Base Trim (${addOns.baseTrimFeet} ft)`,
    amount: addOns.baseTrimFeet * 0.54,
  });
} else {
if (squareFeetRange) {
  const sqft = parseInt(squareFeetRange);
items.push({
    label: `${sqft.toLocaleString()} sq ft`,
    amount: calcHouseSqftPrice(sqft),
  });
}
if (bedrooms > 0) {
  items.push({
      label: `${bedrooms} Bedroom${bedrooms > 1 ? "s" : ""}`,
      amount: bedrooms * 16,
    });
}
if (bathrooms > 0) {
  const fullBaths = Math.floor(bathrooms);
  const hasHalfBath = bathrooms % 1 !== 0;
  let bathCost = fullBaths * 20;
  if (hasHalfBath) bathCost += 13;
  items.push({
      label: `${bathrooms} Bathroom${bathrooms > 1 ? "s" : ""}`,
      amount: bathCost,
    });
}
if (addOns.fridge) items.push({ label: "Inside Fridge", amount: 35 });
if (addOns.oven) items.push({ label: "Inside Oven", amount: 50 });
if (addOns.microwave)
items.push({ label: "Inside Microwave", amount: 10 });
if (addOns.deepClean > 0)
items.push({
    label: `Deep Clean +${addOns.deepClean}hr`,
    amount: addOns.deepClean * 65,
  });
if (addOns.linens > 0)
items.push({
    label: `${addOns.linens} Linen Set${addOns.linens > 1 ? "s" : ""}`,
    amount: addOns.linens * 10,
  });
if (addOns.dishes) items.push({ label: "Clean Dishes", amount: 20 });
if (addOns.windows > 0)
items.push({
    label: `${addOns.windows} Window${addOns.windows > 1 ? "s" : ""}`,
    amount: addOns.windows * 6,
  });
if (addOns.pets > 0)
items.push({
    label: `${addOns.pets} Pet${addOns.pets > 1 ? "s" : ""}`,
    amount: addOns.pets * 10,
  });
if (addOns.baseTrimFeet > 0)
items.push({
    label: `Base Trim (${addOns.baseTrimFeet} ft)`,
    amount: addOns.baseTrimFeet * 0.54,
  });
}
return items;
};
const handleContinueToAddOns = () => {
  const isValid =
  serviceType === "Airbnb Cleaning"
  ? address &&
  serviceType &&
  frequency &&
  airbnbSquareFeet &&
  airbnbLaundry
  : address && serviceType && frequency && squareFeetRange;
  if (isValid) {
    setStep(3);
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
  }, 100);
}
};
const handleSubmit = async (type = 'quote') => {
  // Reentrancy guard — bail synchronously if a submit is already in
  // flight (double-tap, fast re-click). Released in the finally block.
  if (submitLockRef.current) return;
  // Required: at least one preferred service time window. The customer
  // chooses From/To with two dropdowns but has to explicitly click
  // "+ Add this window" to lock it in — easy to miss otherwise. Hard
  // error here keeps the form from submitting with no time committed.
  // (Checked before taking the lock so a missing-window alert can retry.)
  if (timeWindows.length === 0) {
    alert(`Please tap "+ Add this window" to lock in at least one preferred service time before submitting.`);
    // Scroll the time-window section into view so the customer sees the hint.
    setTimeout(() => {
      const hint = document.querySelector('[data-time-windows-section]');
      if (hint) hint.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
    return;
  }
  submitLockRef.current = true;
  setSubmissionType(type);
  setIsSubmitting(true);
  // Build individual add-on lines
  const addonLines = [];
  if (addOns.fridge)           addonLines.push('• Inside Fridge');
  if (addOns.oven)             addonLines.push('• Inside Oven');
  if (addOns.microwave)        addonLines.push('• Inside Microwave');
  if (addOns.deepClean > 0)    addonLines.push(`• Deep Clean +${addOns.deepClean}hr`);
  if (addOns.linens > 0)       addonLines.push(`• ${addOns.linens} Linen Set(s)`);
  if (addOns.dishes)           addonLines.push('• Clean Dishes');
  if (addOns.windows > 0)      addonLines.push(`• ${addOns.windows} Window(s)`);
  if (addOns.pets > 0)         addonLines.push(`• ${addOns.pets} Pet(s)`);
  if (addOns.baseTrimFeet > 0) addonLines.push(`• Base Trim (${addOns.baseTrimFeet} ft)`);

  // Build full price breakdown as formatted string
  const breakdownLines = getPriceBreakdown().map(item =>
    `${item.label.padEnd(30, '.')} $${item.amount.toFixed(2)}`
  ).join('\n');

  const templateParams = {
    // Contact
    first_name:       firstName,
    last_name:        lastName,
    phone:            phone,
    email:            email,
    // Address
    address:          address,
    // Empty string (not 'N/A') so the FlowSTR API's filter(Boolean).join(', ')
    // drops it cleanly — otherwise 'N/A' shows up in the Stripe checkout
    // line-item description ("123 Main · N/A · Anchorage AK").
    address2:         address2 || '',
    city:             city,
    state:            state,
    zip:              zip,
    // Service
    service_type:     serviceType,
    frequency:        frequency,
    // House Cleaning specific (bathrooms included here)
    sqft_range:       serviceType === "House Cleaning" ? squareFeetRange : 'N/A',
    bedrooms:         serviceType === "House Cleaning" ? String(bedrooms) : 'N/A',
    house_bathrooms:  serviceType === "House Cleaning" ? String(bathrooms) : 'N/A',
    // Airbnb specific (bathrooms included here)
    airbnb_sqft:      serviceType === "Airbnb Cleaning" ? airbnbSquareFeet : 'N/A',
    airbnb_laundry:   serviceType === "Airbnb Cleaning" ? airbnbLaundry : 'N/A',
    airbnb_beds:      serviceType === "Airbnb Cleaning" ? String(airbnbBeds) : 'N/A',
    airbnb_bathrooms: serviceType === "Airbnb Cleaning" ? String(bathrooms) : 'N/A',
    airbnb_units:     serviceType === "Airbnb Cleaning" ? airbnbUnits : 'N/A',
    // Add-ons
    addons_list:      addonLines.length > 0 ? addonLines.join('\n') : 'None selected',
    // Notes & scheduling
    key_areas:        keyAreas || 'None specified',
    additional_notes: additionalNotes || 'None',
    preferred_date_1: preferredDay1 || 'Not specified',
    preferred_date_2: preferredDay2 || 'Not specified',
    // handleSubmit guards against empty timeWindows above, so this is
    // always non-empty by the time we get here.
    preferred_times:  timeWindows.join(', '),
    // Pricing — full line-item breakdown
    price_breakdown:  breakdownLines || 'No items',
    subtotal:         `$${calculateSubtotal().toFixed(2)}`,
    discount:         getDiscount() > 0 ? `-$${getDiscount().toFixed(2)}` : 'None',
    discount_label:   getDiscount() > 0 ? (() => {
                        if (serviceType === "House Cleaning") {
                          if (frequency === "every-week")    return "Weekly Discount (20%)";
                          if (frequency === "bi-weekly")     return "Bi-Weekly Discount (15%)";
                          if (frequency === "every-3-weeks") return "3-Week Discount (12%)";
                          if (frequency === "every-4-weeks") return "4-Week Discount (9%)";
                        }
                        return "Discount Applied";
                      })() : 'N/A',
    total_price:      `$${calculateTotal().toFixed(2)}`,
    submission_type:  type,
    // 10% applied to post-frequency-discount total (calculateTotal already has frequency discount applied)
    instant_book_savings: type === 'instant_book' ? (calculateTotal() * 0.10).toFixed(2) : '0',
    instant_book_date: type === 'instant_book' ? instantBookDate : '',
    instant_book_time: type === 'instant_book' ? instantBookTime : '',
  };

  try {
    // Post to CleanSync to create the quote record
    const cleanSyncPayload = {
      ...templateParams,
      instant_book_savings: type === 'instant_book' ? calculateTotal() * 0.10 : 0,
      instant_book_final_price: type === 'instant_book' ? calculateTotal() * 0.90 : calculateTotal(),
      instant_book_date: type === 'instant_book' ? instantBookDate : '',
      instant_book_time: type === 'instant_book' ? instantBookTime : '',
    };

    let quoteId = null;
    try {
      const csRes = await fetch(CLEANSYNC_WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanSyncPayload),
      });
      if (csRes.ok) {
        const csData = await csRes.json();
        quoteId = csData.quoteId;
      }
    } catch {}

    if (type === 'instant_book') {
      // Validate email before opening the pay modal — Stripe will reject
      // anything malformed and we want a clean error here, not from Stripe.
      const cleanEmail = email.trim();
      if (!cleanEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
        alert('Please enter a valid email address before booking.');
        setIsSubmitting(false);
        return;
      }
      if (!quoteId) {
        alert('Could not save your booking. Please try again.');
        setIsSubmitting(false);
        return;
      }
      // Open the embedded-pay modal. The modal handles tip selection and
      // then calls /api/stripe/checkout with embedded:true to get a
      // clientSecret for inline <EmbeddedCheckout/>.
      setPendingQuoteId(quoteId);
      setTipMode('none');
      setTipCustom('');
      setEmbeddedPay(null);
      setPaymentSuccess(false);
      setPayError('');
      setPayModalOpen(true);
      setIsSubmitting(false);
      return;
    }

    // Quote request flow — send email and show success modal
    const result = await window.emailjs.send(
      'service_8bkln92',
      'template_ss9j71d',
      templateParams,
      'ZsAm6x2gjm0hFV69o'
    );
    if (result.status === 200) {
      setShowSuccessModal(true);
    } else {
      alert('There was an error submitting your quote. Please try again or call us directly.');
    }
  } catch (error) {
    console.error('Submit error:', error);
    alert('There was an error submitting your booking. Please try again or call us directly.');
  } finally {
    setIsSubmitting(false);
    submitLockRef.current = false;
  }
};
return (
  <div
  ref={formTopRef}
  style={{
      minHeight: "100vh",
      backgroundColor: "#020c1f",
      backgroundImage: `
        radial-gradient(circle at 20% 30%, rgba(5,53,116,0.55) 0%, transparent 45%),
        radial-gradient(circle at 80% 68%, rgba(10,79,168,0.45) 0%, transparent 40%),
        radial-gradient(circle at 55% 8%, rgba(93,235,241,0.06) 0%, transparent 30%),
        radial-gradient(ellipse at 5% 88%, rgba(5,53,116,0.4) 0%, transparent 40%),
        radial-gradient(circle 1px at center, rgba(255,255,255,0.07) 0%, transparent 100%)
      `,
      backgroundSize: "100% 100%, 100% 100%, 100% 100%, 100% 100%, 26px 26px",
      padding: "20px",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      paddingBottom: mobileBarHeight ? mobileBarHeight + 20 : undefined,
    }}
>
<style>{`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@300;400&family=Allura&display=swap');
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.fade-in-up {
  animation: fadeInUp 0.5s ease-out forwards;
}
.service-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
}
.service-card:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
}
.service-card.selected {
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border: 2px solid #0ea5e9;
  box-shadow: 0 10px 30px rgba(14, 165, 233, 0.3);
}
.counter-btn {
  transition: all 0.2s ease;
}
.counter-btn:active {
  transform: scale(0.9);
}
input:focus, textarea:focus, select:focus {
  outline: none;
  border-color: #0ea5e9;
  box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
}
input, textarea, select { font-size: 16px !important; touch-action: manipulation; }
button { touch-action: manipulation; }
html, body, * { -webkit-text-size-adjust: 100% !important; text-size-adjust: 100% !important; }
input[type="date"] {
  -webkit-appearance: none;
  appearance: none;
  box-sizing: border-box;
  max-width: 100%;
}
.price-breakdown-scroll::-webkit-scrollbar {
  width: 8px;
}
.price-breakdown-scroll::-webkit-scrollbar-track {
  background: #f0f9ff;
  border-radius: 10px;
}
.price-breakdown-scroll::-webkit-scrollbar-thumb {
  background: #0ea5e9;
  border-radius: 10px;
}
.price-breakdown-scroll::-webkit-scrollbar-thumb:hover {
  background: #0284c7;
}
.glow-text {
  color: white;
  text-shadow:
  0 0 10px rgba(255, 255, 255, 0.8),
  0 0 20px rgba(6, 182, 212, 0.6),
  0 0 30px rgba(6, 182, 212, 0.4),
  0 0 40px rgba(6, 182, 212, 0.2);
}

/* Mobile Styles */
@media (max-width: 900px) {
  /* Hide desktop price sidebar on mobile — use sticky bar instead */
  .desktop-sidebar {
    display: none !important;
  }
  
  /* Hide 100% Satisfaction badge on mobile */
  .satisfaction-badge {
    display: none !important;
  }
  
  /* Show mobile price sticky at bottom */
  .mobile-price-sticky {
    display: block !important;
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    z-index: 1000 !important;
    width: 100vw !important;
  }
  
  /* Limit mobile price height so users can see more selections */
  .mobile-price-sticky > div {
    max-height: 37vh !important;
  }
  
  /* Make breakdown scrollable with smaller max height */
  .mobile-price-sticky .price-breakdown-items {
    max-height: 130px !important;
    overflow-y: auto !important;
  }
  
  /* Make main grid single column on mobile with bottom padding for sticky price */
  .mobile-responsive-grid {
    grid-template-columns: 1fr !important;
    
  }
  
  /* Service Type cards - keep 2 columns with better mobile styling */
  .service-grid-2col {
    grid-template-columns: 1fr 1fr !important;
    gap: 12px !important;
  }
  
  /* Service card text - ensure centered and wrap properly on mobile */
  .service-grid-2col .service-card {
    padding: 20px 10px !important;
    text-align: center !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: center !important;
  }
  
  .service-grid-2col .service-card > div:last-child {
    font-size: 14px !important;
    line-height: 1.3 !important;
    word-wrap: break-word !important;
  }
  
  /* City/State/ZIP - make full width on mobile */
  .address-3col {
    grid-template-columns: 1fr !important;
    gap: 12px !important;
  }
  
  /* Turnovers per month - 2 columns on mobile instead of 4 */
  .turnovers-grid {
    grid-template-columns: 1fr 1fr !important;
    gap: 12px !important;
  }
  
  /* Square feet to 2 columns */
  .sqft-grid {
    grid-template-columns: 1fr 1fr !important;
    gap: 10px !important;
  }
  
  /* Add-ons to 2 columns on mobile for better fit */
  .addons-grid {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 12px !important;
  }
  
  /* Time slots to single column */
  .time-grid {
    grid-template-columns: 1fr !important;
    gap: 10px !important;
  }
  
  /* Buttons stack vertically */
  .button-row {
    flex-direction: column !important;
  }
  
  .button-row button {
    width: 100% !important;
  }
}

/* Iframe embedding optimization - full width, no side gaps */
body {
  margin: 0 !important;
  padding: 0 !important;
}

/* Desktop - hide mobile price and ensure sticky works */
.mobile-price-sticky { display: none; }
@media (min-width: 901px) {
  .mobile-price-sticky {
    display: none !important;
  }
  
  /* Desktop iframe embedding - full width */
  .mobile-responsive-grid {
    max-width: 100% !important;
    padding-left: 20px !important;
    padding-right: 20px !important;
  }
}

/* On larger screens in iframe, limit width but keep centered */
@media (min-width: 1440px) {
  .mobile-responsive-grid {
    max-width: 1400px !important;
    margin: 0 auto !important;
  }
}

/* Animated background elements */
.hero-orb {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 0;
}

.hero-orb-1 {
  width: 600px;
  height: 600px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(5,53,116,0.18) 0%, transparent 70%);
  animation: porb 7s ease-in-out infinite;
}

.hero-orb-2 {
  width: 320px;
  height: 320px;
  top: 8%;
  right: 5%;
  background: radial-gradient(circle, rgba(5,53,116,0.14) 0%, transparent 70%);
  animation: porb2 9s ease-in-out infinite reverse;
}

.hero-orb-3 {
  width: 220px;
  height: 220px;
  bottom: 12%;
  left: 5%;
  background: radial-gradient(circle, rgba(93,235,241,0.09) 0%, transparent 70%);
  animation: porb2 6s ease-in-out infinite;
}

@keyframes porb {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.12);
    opacity: 0.7;
  }
}

@keyframes porb2 {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.18);
    opacity: 0.6;
  }
}

.hero-rings {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
}

.hero-dots {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.dot-p {
  position: absolute;
  border-radius: 50%;
  animation: floatUp linear infinite;
}

@keyframes floatUp {
  0%, 100% { transform: translateY(0); opacity: 0.5; }
  50% { transform: translateY(-22px); opacity: 1; }
}
@keyframes pricePop {
  0% { transform: scale(1); }
  50% { transform: scale(1.06); }
  100% { transform: scale(1); }
}
@keyframes savingsPulse {
  0%, 100% { box-shadow: 0 0 10px rgba(16,185,129,0.4), 0 0 20px rgba(16,185,129,0.2); }
  50% { box-shadow: 0 0 18px rgba(16,185,129,0.7), 0 0 35px rgba(16,185,129,0.35); }
}
.price-pop { animation: pricePop 0.35s ease-out forwards; }
.savings-pulse { animation: savingsPulse 2s ease-in-out infinite; }
`}</style>

{/* Animated Background Elements - Fixed Container */}
<div style={{
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflow: "hidden",
  pointerEvents: "none",
  zIndex: 0
}}>
<div className="hero-orb hero-orb-1"></div>
<div className="hero-orb hero-orb-2"></div>
<div className="hero-orb hero-orb-3"></div>

<div className="hero-rings">
  <svg width="560" height="560" style={{position:"absolute",top:"-100px",left:"28%",opacity:0.5}} viewBox="0 0 560 560">
    <circle cx="280" cy="280" r="240" fill="none" stroke="rgba(93,235,241,0.5)" strokeWidth="1.8"/>
    <circle cx="280" cy="280" r="190" fill="none" stroke="rgba(14,165,233,0.3)" strokeWidth="1.2"/>
    <circle cx="280" cy="280" r="140" fill="none" stroke="rgba(93,235,241,0.35)" strokeWidth="1.2"/>
    <circle cx="280" cy="280" r="90" fill="none" stroke="rgba(14,165,233,0.2)" strokeWidth="0.8"/>
  </svg>
  <svg width="300" height="300" style={{position:"absolute",bottom:"-60px",right:"4%",opacity:0.45}} viewBox="0 0 300 300">
    <circle cx="150" cy="150" r="125" fill="none" stroke="rgba(93,235,241,0.45)" strokeWidth="1.8"/>
    <circle cx="150" cy="150" r="80" fill="none" stroke="rgba(14,165,233,0.3)" strokeWidth="1.2"/>
  </svg>
</div>

<div className="hero-dots">
  <div className="dot-p" style={{width:"7px",height:"7px",left:"22%",bottom:"12%",animationDuration:"7s",animationDelay:"0s",background:"rgba(93,235,241,0.6)",boxShadow:"0 0 10px rgba(93,235,241,0.5)"}}></div>
  <div className="dot-p" style={{width:"5px",height:"5px",left:"48%",bottom:"18%",animationDuration:"9s",animationDelay:"1.5s",background:"rgba(14,165,233,0.5)",boxShadow:"0 0 8px rgba(14,165,233,0.5)"}}></div>
  <div className="dot-p" style={{width:"8px",height:"8px",left:"72%",bottom:"9%",animationDuration:"8s",animationDelay:"0.7s",background:"rgba(93,235,241,0.55)",boxShadow:"0 0 12px rgba(93,235,241,0.5)"}}></div>
  <div className="dot-p" style={{width:"6px",height:"6px",left:"33%",bottom:"22%",animationDuration:"10s",animationDelay:"2s",background:"rgba(14,165,233,0.45)",boxShadow:"0 0 10px rgba(14,165,233,0.4)"}}></div>
  <div className="dot-p" style={{width:"6px",height:"6px",left:"82%",bottom:"28%",animationDuration:"7.5s",animationDelay:"3s",background:"rgba(93,235,241,0.5)",boxShadow:"0 0 10px rgba(93,235,241,0.5)"}}></div>
</div>
</div>

<div
className="mobile-responsive-grid"
style={{
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "30px 20px",
    display: "grid",
    gridTemplateColumns: step === 1 ? "1fr" : "1fr 380px",
    gap: "30px",
    alignItems: "start",
    position: "relative",
    zIndex: 1,
  }}
>
{/* Main Form */}
<div
className="form-card-animated"
style={{
    backgroundColor: "#020c1f",
    backgroundImage: `
      radial-gradient(circle at 20% 30%, rgba(5,53,116,0.55) 0%, transparent 45%),
      radial-gradient(circle at 80% 68%, rgba(10,79,168,0.45) 0%, transparent 40%),
      radial-gradient(circle at 55% 8%, rgba(93,235,241,0.06) 0%, transparent 30%),
      radial-gradient(ellipse at 5% 88%, rgba(5,53,116,0.4) 0%, transparent 40%),
      radial-gradient(circle 1px at center, rgba(255,255,255,0.07) 0%, transparent 100%)
    `,
    backgroundSize: "100% 100%, 100% 100%, 100% 100%, 100% 100%, 26px 26px",
    borderRadius: "32px",
    overflow: "hidden",
    boxShadow: "0 30px 80px rgba(0, 0, 0, 0.5)",
    border: "1px solid rgba(93, 235, 241, 0.2)",
    position: "relative",
  }}
>
{/* Header with Custom Animated Title */}
<div
style={{
    backgroundColor: "rgba(2, 12, 31, 0.4)",
    backdropFilter: "blur(10px)",
    borderBottom: "1px solid rgba(93, 235, 241, 0.2)",
    padding: step === 1 ? "50px 30px" : "30px",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  }}
>
{/* Decorative rings inside header */}
<svg width="220" height="220" style={{position:"absolute",top:"-60px",left:"-40px",opacity:0.2,pointerEvents:"none"}} viewBox="0 0 220 220">
  <circle cx="110" cy="110" r="100" fill="none" stroke="rgba(93,235,241,0.8)" strokeWidth="1.2"/>
  <circle cx="110" cy="110" r="70" fill="none" stroke="rgba(125,211,252,0.5)" strokeWidth="0.8"/>
</svg>
<svg width="180" height="180" style={{position:"absolute",bottom:"-50px",right:"-30px",opacity:0.2,pointerEvents:"none"}} viewBox="0 0 180 180">
  <circle cx="90" cy="90" r="80" fill="none" stroke="rgba(93,235,241,0.8)" strokeWidth="1.2"/>
  <circle cx="90" cy="90" r="52" fill="none" stroke="rgba(125,211,252,0.5)" strokeWidth="0.8"/>
</svg>
{/* Floating dots */}
<div style={{position:"absolute",width:"5px",height:"5px",borderRadius:"50%",background:"rgba(125,211,252,0.7)",top:"20%",left:"12%",boxShadow:"0 0 6px rgba(93,235,241,0.6)",pointerEvents:"none"}}/>
<div style={{position:"absolute",width:"4px",height:"4px",borderRadius:"50%",background:"rgba(93,235,241,0.6)",top:"60%",left:"80%",boxShadow:"0 0 5px rgba(93,235,241,0.5)",pointerEvents:"none"}}/>
<div style={{position:"absolute",width:"3px",height:"3px",borderRadius:"50%",background:"rgba(125,211,252,0.5)",top:"75%",left:"25%",boxShadow:"0 0 4px rgba(93,235,241,0.4)",pointerEvents:"none"}}/>
<div style={{position:"absolute",width:"4px",height:"4px",borderRadius:"50%",background:"rgba(93,235,241,0.5)",top:"30%",left:"60%",boxShadow:"0 0 5px rgba(93,235,241,0.4)",pointerEvents:"none"}}/>
<div
style={{
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
    "radial-gradient(circle at 50% 50%, rgba(14, 165, 233, 0.2) 0%, transparent 70%)",
    pointerEvents: "none",
  }}
/>
<div style={{ position: "relative", zIndex: 1 }}>
{/* Custom Title - Logo Style */}
<div
style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "8px",
  }}
>
{/* CLEANING - Thin modern all caps */}
<div
className="glow-text"
style={{
    fontFamily: "'Oswald', sans-serif",
    fontSize: "52px",
    fontWeight: "300",
    letterSpacing: "8px",
    lineHeight: "1",
    color: "white",
    textShadow: "0 0 20px rgba(255,255,255,0.6), 0 0 40px rgba(93,235,241,0.3)",
  }}
>
CLEANING
</div>
{/* Su Casa - Elegant handwritten cursive */}
<div
style={{
    fontFamily: "'Allura', cursive",
    fontSize: "56px",
    letterSpacing: "3px",
    marginTop: "-8px",
    lineHeight: "1",
    fontWeight: "400",
    background: "linear-gradient(180deg, #E0F7FF 0%, #7dd3fc 25%, #0369a1 50%, #7dd3fc 75%, #E0F7FF 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  }}
>
Su Casa
</div>
</div>
{step === 1 && (
    <>
    <div
    style={{
        height: "2px",
        width: "80px",
        background: "linear-gradient(90deg, transparent, #7dd3fc, transparent)",
        margin: "14px auto 10px",
        boxShadow: "0 0 10px rgba(125,211,252,0.5)",
      }}
  />
  <p
  style={{
      color: "rgba(125,211,252,0.7)",
      fontSize: "13px",
      margin: 0,
      fontWeight: "700",
      letterSpacing: "2px",
      textTransform: "uppercase",
    }}
>
🏠 Home Cleaning Quote
</p>
</>
)}
</div>
</div>
{/* Progress Bar */}
<div
style={{
    height: "6px",
    background: "rgba(255, 255, 255, 0.1)",
    position: "relative",
  }}
>
<div
style={{
    height: "100%",
    background: "linear-gradient(90deg, #06b6d4 0%, #0ea5e9 100%)",
    width: step === 1 ? "33%" : step === 2 ? "66%" : "100%",
    transition: "width 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: "0 0 15px rgba(6, 182, 212, 0.6)",
  }}
/>
</div>
<div style={{ padding: "50px 40px" }}>
{/* Step 1: Email */}
{step === 1 && (
    <div className="fade-in-up">
    <div style={{ textAlign: "center", marginBottom: "50px" }}>
    <div
    style={{
        width: "110px",
        height: "110px",
        background:
        "linear-gradient(135deg, #06b6d4 0%, #0ea5e9 100%)",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 30px",
        boxShadow: "0 20px 50px rgba(6, 182, 212, 0.4)",
        border: "4px solid rgba(255, 255, 255, 0.2)",
      }}
  >
  <Sparkles size={55} color="white" strokeWidth={2.5} />
  </div>
  <h2
  style={{
      fontSize: "36px",
      fontWeight: "900",
      color: "white",
      margin: "0 0 20px 0",
      letterSpacing: "-1px",
      textTransform: "uppercase",
    }}
>
Let's Do This!
</h2>
<p
style={{
    color: "rgba(255, 255, 255, 0.8)",
    fontSize: "18px",
    lineHeight: "1.6",
    fontWeight: "500",
  }}
>
Drop your email & let's build your perfect clean
</p>
</div>
<div style={{ maxWidth: "500px", margin: "0 auto" }}>
{/* Name Fields */}
<div
style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
    marginBottom: "25px",
  }}
>
<div>
<label
style={{
    display: "block",
    fontSize: "13px",
    fontWeight: "800",
    color: "#06b6d4",
    marginBottom: "12px",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
  }}
>
First Name
</label>
<input
type="text"
value={firstName}
onChange={(e) => setFirstName(e.target.value)}
placeholder="John"
style={{
    width: "100%",
    padding: "20px 24px",
    fontSize: "17px",
    border: "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "16px",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    background: "rgba(255, 255, 255, 0.95)",
    fontWeight: "500",
  }}
/>
</div>
<div>
<label
style={{
    display: "block",
    fontSize: "13px",
    fontWeight: "800",
    color: "#06b6d4",
    marginBottom: "12px",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
  }}
>
Last Name
</label>
<input
type="text"
value={lastName}
onChange={(e) => setLastName(e.target.value)}
placeholder="Doe"
style={{
    width: "100%",
    padding: "20px 24px",
    fontSize: "17px",
    border: "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "16px",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    background: "rgba(255, 255, 255, 0.95)",
    fontWeight: "500",
  }}
/>
</div>
</div>
{/* Phone Field */}
<div style={{ marginBottom: "25px" }}>
<label
style={{
    display: "block",
    fontSize: "13px",
    fontWeight: "800",
    color: "#06b6d4",
    marginBottom: "12px",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
  }}
>
Phone Number
</label>
<input
type="tel"
value={phone}
onChange={(e) => setPhone(e.target.value)}
placeholder="(555) 123-4567"
style={{
    width: "100%",
    padding: "20px 24px",
    fontSize: "17px",
    border: "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "16px",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    background: "rgba(255, 255, 255, 0.95)",
    fontWeight: "500",
  }}
/>
</div>
{/* Email Field */}
<div style={{ marginBottom: "40px" }}>
<label
style={{
    display: "block",
    fontSize: "13px",
    fontWeight: "800",
    color: "#06b6d4",
    marginBottom: "12px",
    letterSpacing: "1.5px",
    textTransform: "uppercase",
  }}
>
Email Address *
</label>
<input
type="email"
value={email}
onChange={(e) => setEmail(e.target.value)}
placeholder="your.email@example.com"
required
style={{
    width: "100%",
    padding: "20px 24px",
    fontSize: "17px",
    border: email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
      ? "2px solid rgba(239,68,68,0.7)"
      : email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
      ? "2px solid rgba(16,185,129,0.7)"
      : "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "16px",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    background: "rgba(255, 255, 255, 0.95)",
    fontWeight: "500",
  }}
/>
{email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && (
  <div style={{ fontSize: "12px", color: "rgba(239,68,68,0.9)", fontWeight: "700", marginTop: "6px", paddingLeft: "4px" }}>
    ⚠ Please enter a valid email address (e.g. name@example.com)
  </div>
)}
{email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && (
  <div style={{ fontSize: "12px", color: "#10b981", fontWeight: "700", marginTop: "6px", paddingLeft: "4px" }}>
    ✓ Looks good!
  </div>
)}
</div>
</div>
<button
onClick={() => {
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  if (!firstName.trim()) { alert('Please enter your first name.'); return }
  if (!lastName.trim())  { alert('Please enter your last name.'); return }
  if (!email.trim())     { alert('Please enter your email address.'); return }
  if (!validEmail)       { alert('Please enter a valid email address (e.g. name@example.com).'); return }
  setStep(2);
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, 100);
}}
disabled={!firstName.trim() || !lastName.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())}
style={{
    width: "100%",
    maxWidth: "500px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    margin: "0 auto",
    padding: "22px",
    background: email
    ? "linear-gradient(135deg, #06b6d4 0%, #0ea5e9 100%)"
    : "rgba(255, 255, 255, 0.2)",
    color: "white",
    border: "none",
    borderRadius: "16px",
    fontSize: "18px",
    fontWeight: "800",
    cursor: email ? "pointer" : "not-allowed",
    transition: "all 0.3s ease",
    boxShadow: email
    ? "0 15px 40px rgba(6, 182, 212, 0.4)"
    : "none",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  }}
onMouseEnter={(e) => {
    if (email) {
      e.target.style.transform = "translateY(-2px)";
      e.target.style.boxShadow =
      "0 20px 50px rgba(6, 182, 212, 0.5)";
    }
}}
onMouseLeave={(e) => {
    e.target.style.transform = "translateY(0)";
    e.target.style.boxShadow = email
    ? "0 15px 40px rgba(6, 182, 212, 0.4)"
    : "none";
  }}
>
<span>To Service Details</span>
<span style={{ fontSize: "24px", marginLeft: "8px" }}>→</span>
</button>
</div>
)}
{/* Step 2: Service Selection - CONDITIONAL RENDERING */}
{step === 2 && (
    <div className="fade-in-up">
    <h2
    style={{
        fontSize: "32px",
        fontWeight: "900",
        color: "white",
        margin: "0 0 40px 0",
        letterSpacing: "-0.5px",
        textTransform: "uppercase",
      }}
  >
  Service Details
  </h2>
  {/* Address */}
  <div style={{ marginBottom: "35px" }}>
  <label
  style={{
      display: "flex",
      alignItems: "center",
      fontSize: "13px",
      fontWeight: "800",
      color: "#06b6d4",
      marginBottom: "12px",
      gap: "8px",
      letterSpacing: "1px",
      textTransform: "uppercase",
    }}
>
<MapPin size={18} color="#06b6d4" />
Service Address *
</label>
{/* Street Address */}
<input
ref={addressInputRef}
type="text"
value={address}
onChange={(e) => setAddress(e.target.value)}
placeholder="Start typing your address..."
style={{
    width: "100%",
    padding: "20px 24px",
    fontSize: "17px",
    border: "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "16px",
    boxSizing: "border-box",
    background: "rgba(255, 255, 255, 0.95)",
    fontWeight: "500",
    marginBottom: "15px",
  }}
/>
{/* Address Line 2 */}
<input
type="text"
value={address2}
onChange={(e) => setAddress2(e.target.value)}
placeholder="Apt, Suite, Unit (optional)"
style={{
    width: "100%",
    padding: "20px 24px",
    fontSize: "17px",
    border: "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "16px",
    boxSizing: "border-box",
    background: "rgba(255, 255, 255, 0.95)",
    fontWeight: "500",
    marginBottom: "15px",
  }}
/>
{/* City, State, Zip */}
<div
className="address-3col"
style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "15px",
  }}
>
<input
type="text"
value={city}
onChange={(e) => setCity(e.target.value)}
placeholder="City"
style={{
    width: "100%",
    padding: "20px 24px",
    fontSize: "17px",
    border: "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "16px",
    boxSizing: "border-box",
    background: "rgba(255, 255, 255, 0.95)",
    fontWeight: "500",
  }}
/>
<input
type="text"
value={state}
onChange={(e) => setState(e.target.value)}
placeholder="State"
maxLength="2"
style={{
    width: "100%",
    padding: "20px 24px",
    fontSize: "17px",
    border: "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "16px",
    boxSizing: "border-box",
    background: "rgba(255, 255, 255, 0.95)",
    fontWeight: "500",
    textTransform: "uppercase",
  }}
/>
<input
type="text"
value={zip}
onChange={(e) => setZip(e.target.value)}
placeholder="ZIP"
maxLength="5"
style={{
    width: "100%",
    padding: "20px 24px",
    fontSize: "17px",
    border: "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "16px",
    boxSizing: "border-box",
    background: "rgba(255, 255, 255, 0.95)",
    fontWeight: "500",
  }}
/>
</div>
<p
style={{
    fontSize: "13px",
    color: "rgba(255, 255, 255, 0.7)",
    marginTop: "10px",
    marginBottom: 0,
    fontWeight: "600",
  }}
>
📍 Start typing street address for suggestions
</p>
</div>
{/* Service Type */}
<div style={{ marginBottom: "35px" }}>
<label
style={{
    display: "flex",
    alignItems: "center",
    fontSize: "13px",
    fontWeight: "800",
    color: "#06b6d4",
    marginBottom: "15px",
    gap: "8px",
    letterSpacing: "1px",
    textTransform: "uppercase",
  }}
>
<Home size={18} color="#06b6d4" />
Service Type *
</label>
<div
className="service-grid-2col"
style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
  }}
>
{["House Cleaning", "Airbnb Cleaning"].map((type) => (
      <div
      key={type}
      className={`service-card ${
          serviceType === type ? "selected" : ""
        }`}
    onClick={() => {
        setServiceType(type);
        setFrequency("");
        // Reset opposite type's fields
        if (type === "House Cleaning") {
          setAirbnbSquareFeet("");
          setAirbnbLaundry("");
          setAirbnbBeds(0);
        } else {
        setSquareFeetRange("");
        setBedrooms(0);
      }
  }}
style={{
    padding: "30px",
    border:
    serviceType === type
    ? "2px solid #0ea5e9"
    : "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    textAlign: "center",
    background:
    serviceType === type
    ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
    : "rgba(255, 255, 255, 0.95)",
  }}
>
<div
style={{
    fontSize: "52px",
    marginBottom: "15px",
  }}
>
{type === "House Cleaning" ? "🏠" : "🏡"}
</div>
<div
style={{
    fontSize: "17px",
    fontWeight: "800",
    color: serviceType === type ? "white" : "#0c4a6e",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  }}
>
{type}
</div>
</div>
))}
</div>
</div>
{/* Frequency - Different styles based on service type */}
<div style={{ marginBottom: "35px" }}>
<label
style={{
    display: "flex",
    alignItems: "center",
    fontSize: "13px",
    fontWeight: "800",
    color: "#06b6d4",
    marginBottom: "12px",
    gap: "8px",
    letterSpacing: "1px",
    textTransform: "uppercase",
  }}
>
<Calendar size={18} color="#06b6d4" />
{serviceType === "Airbnb Cleaning"
  ? "Turnovers Per Month *"
  : "Frequency *"}
</label>
{serviceType === "House Cleaning" ? (
  <Dropdown
    value={frequency}
    onChange={setFrequency}
    placeholder="Select frequency..."
    options={[
      { value: "every-week",    label: "Every Week (20% discount)" },
      { value: "bi-weekly",     label: "Bi-Weekly (15% discount)" },
      { value: "every-3-weeks", label: "Every 3 Weeks (12% discount)" },
      { value: "every-4-weeks", label: "Every 4 Weeks (9% discount)" },
      { value: "one-time",      label: "One-Time (no discount)" },
    ]}
  />
) : (
<div
className="turnovers-grid"
style={{
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "15px",
  }}
>
{[
    { value: "1-3", label: "1-3", discount: "" },
    { value: "4-6", label: "4-6", discount: "3% off" },
    { value: "7-9", label: "7-9", discount: "6% off" },
    { value: "10+", label: "10+", discount: "7.5% off" },
  ].map((option) => (
    <div
    key={option.value}
    className={`service-card ${
        frequency === option.value ? "selected" : ""
      }`}
  onClick={() => setFrequency(option.value)}
  style={{
      padding: "20px 15px",
      border:
      frequency === option.value
      ? "2px solid #0ea5e9"
      : "2px solid rgba(255, 255, 255, 0.2)",
      borderRadius: "16px",
      textAlign: "center",
      background:
      frequency === option.value
      ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
      : "rgba(255, 255, 255, 0.95)",
      cursor: "pointer",
    }}
>
<div
style={{
    fontSize: "22px",
    fontWeight: "900",
    color:
    frequency === option.value
    ? "white"
    : "#0c4a6e",
    marginBottom: "8px",
  }}
>
{option.label}
</div>
{option.discount && (
    <div
    style={{
        fontSize: "11px",
        fontWeight: "700",
        color:
        frequency === option.value
        ? "rgba(255,255,255,0.9)"
        : "#10b981",
      }}
  >
  {option.discount}
  </div>
)}
</div>
))}
</div>
)}
</div>
{/* Airbnb Units Selection */}
{serviceType === "Airbnb Cleaning" && (
    <div style={{ marginBottom: "35px" }}>
    <label
    style={{
        display: "flex",
        alignItems: "center",
        fontSize: "13px",
        fontWeight: "800",
        color: "#06b6d4",
        marginBottom: "12px",
        gap: "8px",
        letterSpacing: "1px",
        textTransform: "uppercase",
      }}
  >
  <Home size={18} color="#06b6d4" />
  Number of Units
  </label>
  <div
  style={{
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: "8px",
    }}
>
{[
    { value: "1", label: "1 Unit", discount: "" },
    { value: "2", label: "2 Units", discount: "3% off" },
    { value: "3", label: "3 Units", discount: "5% off" },
    { value: "4+", label: "4+ Units", discount: "7% off" },
  ].map((option) => (
    <div
    key={option.value}
    className={`service-card ${
        airbnbUnits === option.value ? "selected" : ""
      }`}
  onClick={() => setAirbnbUnits(option.value)}
  style={{
      padding: "16px 8px",
      border:
      airbnbUnits === option.value
      ? "2px solid #0ea5e9"
      : "2px solid rgba(255, 255, 255, 0.2)",
      borderRadius: "16px",
      textAlign: "center",
      background:
      airbnbUnits === option.value
      ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
      : "rgba(255, 255, 255, 0.95)",
      cursor: "pointer",
      minWidth: 0,
    }}
>
<div
style={{
    fontSize: "12px",
    fontWeight: "800",
    color:
    airbnbUnits === option.value
    ? "white"
    : "#0c4a6e",
    marginBottom: option.discount ? "4px" : "0",
    lineHeight: "1.3",
    wordBreak: "break-word",
  }}
>
{option.label}
</div>
{option.discount && (
    <div
    style={{
        fontSize: "10px",
        fontWeight: "700",
        color:
        airbnbUnits === option.value
        ? "rgba(255,255,255,0.9)"
        : "#10b981",
      }}
  >
  {option.discount}
  </div>
)}
</div>
))}
</div>
</div>
)}
{/* HOUSE CLEANING FIELDS */}
{serviceType === "House Cleaning" && (
    <>
    {/* Square Feet - Slider */}
    <div style={{ marginBottom: "35px" }}>
    <label
    style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        fontSize: "13px",
        fontWeight: "800",
        color: "#06b6d4",
        marginBottom: "20px",
        gap: "8px",
        letterSpacing: "1px",
        textTransform: "uppercase",
        flexWrap: "wrap",
      }}
  >
    <div style={{ display:"flex", alignItems:"center", gap:"8px" }}><span>📐</span>Home Size *</div>
    <div style={{ fontSize:"22px", fontWeight:"900", color:"white" }}>{parseInt(squareFeetRange||500).toLocaleString()} sqft</div>
  </label>
  <input
    type="range"
    min="500"
    max="6500"
    step="50"
    value={squareFeetRange || 500}
    onChange={e => setSquareFeetRange(e.target.value)}
    style={{
      width:"100%", height:"8px", borderRadius:"4px", outline:"none",
      cursor:"pointer", WebkitAppearance:"none", appearance:"none", marginBottom:"16px",
      background:`linear-gradient(to right, #0ea5e9 0%, #0ea5e9 ${((parseInt(squareFeetRange||500)-500)/(6500-500))*100}%, rgba(255,255,255,0.2) ${((parseInt(squareFeetRange||500)-500)/(6500-500))*100}%, rgba(255,255,255,0.2) 100%)`
    }}
  />
  <div style={{ position:"relative" }}>
    <input
      type="number"
      min="500"
      max="6500"
      value={squareFeetRange || ""}
      onChange={e => setSquareFeetRange(e.target.value)}
      placeholder="Enter square footage..."
      style={{
        width:"100%", padding:"20px 60px 20px 24px",
        fontSize:"17px", border:"2px solid rgba(255,255,255,0.2)",
        borderRadius:"16px", boxSizing:"border-box",
        background:"rgba(255,255,255,0.95)", fontWeight:"500", color:"#0c4a6e",
      }}
    />
    <div style={{ position:"absolute", right:"20px", top:"50%", transform:"translateY(-50%)", color:"#0369a1", fontSize:"14px", fontWeight:"700", pointerEvents:"none" }}>sqft</div>
  </div>
  {/* Live price display */}
  {squareFeetRange && (
    <div style={{ marginTop:"16px" }}>
      {/* Main price box */}
      <div style={{ padding:"18px 22px", borderRadius:"14px", background:"rgba(14,165,233,0.12)", border:"1px solid rgba(93,235,241,0.3)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ color:"rgba(255,255,255,0.6)", fontSize:"12px", fontWeight:"700", letterSpacing:"1px", textTransform:"uppercase" }}>
          {frequency && frequencyDiscounts[frequency] > 0 ? "Your discounted base cost" : "Base cost"}
        </div>
        <div key={`${squareFeetRange}-${frequency}`} className="price-pop" style={{ textAlign:"right" }}>
          {frequency && frequencyDiscounts[frequency] > 0 && (
            <div style={{ color:"rgba(255,255,255,0.35)", fontSize:"12px", fontWeight:"600", textDecoration:"line-through", marginBottom:"2px" }}>
              was ${calcHouseSqftPrice(parseInt(squareFeetRange)).toFixed(2)}
            </div>
          )}
          <div style={{ color:"#7dd3fc", fontSize:"32px", fontWeight:"900", lineHeight:"1", textShadow:"0 0 20px rgba(125,211,252,0.5)" }}>
            ${(calcHouseSqftPrice(parseInt(squareFeetRange||500)) * (1-(frequencyDiscounts[frequency]||0))).toFixed(2)}
          </div>
          <div style={{ color:"rgba(255,255,255,0.4)", fontSize:"11px", fontWeight:"600" }}>base cost</div>
        </div>
      </div>
      {/* Glowing savings badge — only when discount applies */}
      {frequency && frequencyDiscounts[frequency] > 0 && (
        <div className="savings-pulse" style={{ marginTop:"10px", padding:"12px 20px", borderRadius:"12px", background:"linear-gradient(135deg, rgba(16,185,129,0.2), rgba(5,150,105,0.15))", border:"1.5px solid rgba(16,185,129,0.5)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
            <span style={{ fontSize:"16px" }}>✓</span>
            <div>
              <div style={{ color:"#34d399", fontSize:"13px", fontWeight:"800", letterSpacing:"0.5px" }}>
                You Save {Math.round(frequencyDiscounts[frequency]*100)}%
              </div>
              <div style={{ color:"rgba(52,211,153,0.7)", fontSize:"11px", fontWeight:"600" }}>
                {frequency === "every-week" ? "Every Week" : frequency === "bi-weekly" ? "Bi-Weekly" : frequency === "every-3-weeks" ? "Every 3 Weeks" : "Every 4 Weeks"} discount applied
              </div>
            </div>
          </div>
          <div style={{ color:"#34d399", fontSize:"20px", fontWeight:"900", textShadow:"0 0 12px rgba(52,211,153,0.6)", whiteSpace:"nowrap", flexShrink:0 }}>
            -${(calcHouseSqftPrice(parseInt(squareFeetRange)) * frequencyDiscounts[frequency]).toFixed(2)}
          </div>
        </div>
      )}
    </div>
  )}
  </div>
{/* Bedrooms */}
<div style={{ marginBottom: "35px" }}>
<label
style={{
    display: "flex",
    alignItems: "center",
    fontSize: "13px",
    fontWeight: "800",
    color: "#06b6d4",
    marginBottom: "15px",
    gap: "8px",
    letterSpacing: "1px",
    textTransform: "uppercase",
  }}
>
<Bed size={18} color="#06b6d4" />
Bedrooms
</label>
<div
style={{
    display: "flex",
    alignItems: "center",
    gap: "20px",
    padding: "28px",
    background: "rgba(255, 255, 255, 0.95)",
    borderRadius: "20px",
    border: "2px solid rgba(255, 255, 255, 0.3)",
  }}
>
<div style={{ fontSize: "44px" }}>🛏️</div>
<button
className="counter-btn"
onClick={() => setBedrooms(Math.max(0, bedrooms - 1))}
style={{
    width: "52px",
    height: "52px",
    background: "white",
    border: "3px solid #0284c7",
    borderRadius: "14px",
    fontSize: "28px",
    fontWeight: "800",
    color: "#0284c7",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(2, 132, 199, 0.15)",
  }}
>
−
</button>
<div
style={{
    flex: 1,
    textAlign: "center",
    fontSize: "42px",
    fontWeight: "900",
    color: "#0c4a6e",
  }}
>
{bedrooms}
</div>
<button
className="counter-btn"
onClick={() => setBedrooms(bedrooms + 1)}
style={{
    width: "52px",
    height: "52px",
    background:
    "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
    border: "none",
    borderRadius: "14px",
    fontSize: "28px",
    fontWeight: "800",
    color: "white",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 6px 16px rgba(14, 165, 233, 0.3)",
  }}
>
+
</button>
</div>
</div>
</>
)}
{/* AIRBNB CLEANING FIELDS */}
{serviceType === "Airbnb Cleaning" && (
    <>
    {/* Laundry Selection */}
    <div style={{ marginBottom: "35px" }}>
    <label
    style={{
        display: "flex",
        alignItems: "center",
        fontSize: "13px",
        fontWeight: "800",
        color: "#06b6d4",
        marginBottom: "15px",
        gap: "8px",
        letterSpacing: "1px",
        textTransform: "uppercase",
      }}
  >
  <Sparkles size={18} color="#06b6d4" />
  Laundry Service *
  </label>
  <div
  style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "20px",
    }}
>
{[
    { value: "yes", label: "With Laundry" },
    { value: "no", label: "No Laundry" },
  ].map((option) => (
    <div
    key={option.value}
    className={`service-card ${
        airbnbLaundry === option.value ? "selected" : ""
      }`}
  onClick={() => setAirbnbLaundry(option.value)}
  style={{
      padding: "25px",
      border:
      airbnbLaundry === option.value
      ? "2px solid #0ea5e9"
      : "2px solid rgba(255, 255, 255, 0.2)",
      borderRadius: "20px",
      textAlign: "center",
      background:
      airbnbLaundry === option.value
      ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
      : "rgba(255, 255, 255, 0.95)",
    }}
>
<div
style={{
    fontSize: "16px",
    fontWeight: "800",
    color:
    airbnbLaundry === option.value
    ? "white"
    : "#0c4a6e",
    letterSpacing: "0.5px",
  }}
>
{option.label}
</div>
</div>
))}
</div>
</div>
{/* Square Footage Input */}
<div style={{ marginBottom: "35px" }}>
<label
style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: "13px",
    fontWeight: "800",
    color: "#06b6d4",
    marginBottom: "12px",
    gap: "8px",
    letterSpacing: "1px",
    textTransform: "uppercase",
  }}
>
  <div style={{ display:"flex", alignItems:"center", gap:"8px" }}><span>📐</span>Square Footage *</div>
  {airbnbSquareFeet && <div style={{ fontSize:"20px", fontWeight:"900", color:"white" }}>{parseInt(airbnbSquareFeet).toLocaleString()} sqft</div>}
</label>
{airbnbLaundry && (
  <>
    <input
      type="range"
      min="200"
      max="6000"
      step="50"
      value={airbnbSquareFeet || 200}
      onChange={e => setAirbnbSquareFeet(e.target.value)}
      style={{
        width: "100%", height: "8px", borderRadius: "4px", outline: "none",
        cursor: "pointer", WebkitAppearance: "none", appearance: "none", marginBottom: "16px",
        background: `linear-gradient(to right, #0ea5e9 0%, #0ea5e9 ${((parseInt(airbnbSquareFeet || 200) - 200) / (6000 - 200)) * 100}%, rgba(255,255,255,0.2) ${((parseInt(airbnbSquareFeet || 200) - 200) / (6000 - 200)) * 100}%, rgba(255,255,255,0.2) 100%)`
      }}
    />
    <style>{`input[type="range"]::-webkit-slider-thumb{-webkit-appearance:none;width:24px;height:24px;border-radius:50%;background:linear-gradient(135deg,#0284c7,#0ea5e9);cursor:pointer;border:3px solid white;box-shadow:0 4px 12px rgba(14,165,233,0.5);}input[type="range"]::-moz-range-thumb{width:24px;height:24px;border-radius:50%;background:linear-gradient(135deg,#0284c7,#0ea5e9);cursor:pointer;border:3px solid white;}`}</style>
  </>
)}
<div style={{ position:"relative" }}>
  <input
    type="number"
    value={airbnbSquareFeet}
    onChange={(e) => setAirbnbSquareFeet(e.target.value)}
    placeholder="Enter square footage"
    disabled={!airbnbLaundry}
    style={{
      width: "100%", padding: "20px 60px 20px 24px",
      fontSize: "17px", border: "2px solid rgba(255, 255, 255, 0.2)",
      borderRadius: "16px", boxSizing: "border-box",
      background: airbnbLaundry ? "rgba(255, 255, 255, 0.95)" : "rgba(255, 255, 255, 0.5)",
      fontWeight: "500", cursor: airbnbLaundry ? "text" : "not-allowed",
      opacity: airbnbLaundry ? 1 : 0.6,
    }}
  />
  <div style={{ position:"absolute", right:"20px", top:"50%", transform:"translateY(-50%)", color:"#0369a1", fontSize:"14px", fontWeight:"700", pointerEvents:"none" }}>sqft</div>
</div>
{!airbnbLaundry && (
    <p style={{ fontSize: "13px", color: "rgba(255, 255, 255, 0.7)", marginTop: "10px", marginBottom: 0, fontWeight: "600" }}>
    💡 Please select laundry service first
    </p>
)}
{/* Live price display for Airbnb sqft */}
{airbnbLaundry && airbnbSquareFeet && (() => {
  const rate = airbnbLaundry === "yes" ? 0.095 : 0.09;
  const sqftCost = parseFloat(airbnbSquareFeet) * rate;
  const discountPercent = (frequency === "4-6" ? 0.03 : frequency === "7-9" ? 0.06 : frequency === "10+" ? 0.075 : 0) +
    (airbnbUnits === "2" ? 0.03 : airbnbUnits === "3" ? 0.05 : airbnbUnits === "4+" ? 0.07 : 0);
  const discountedSqftCost = sqftCost * (1 - discountPercent);
  return (
    <div style={{ marginTop:"16px" }}>
      <div style={{ padding:"18px 22px", borderRadius:"14px", background:"rgba(14,165,233,0.12)", border:"1px solid rgba(93,235,241,0.3)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ color:"rgba(255,255,255,0.6)", fontSize:"12px", fontWeight:"700", letterSpacing:"1px", textTransform:"uppercase" }}>
          {discountPercent > 0 ? "Discounted base cost" : "Base cost"}
        </div>
        <div key={`${airbnbSquareFeet}-${airbnbLaundry}-${discountPercent}`} className="price-pop" style={{ textAlign:"right" }}>
          {discountPercent > 0 && (
            <div style={{ color:"rgba(255,255,255,0.35)", fontSize:"12px", fontWeight:"600", textDecoration:"line-through", marginBottom:"2px" }}>
              was ${sqftCost.toFixed(2)}
            </div>
          )}
          <div style={{ color:"#7dd3fc", fontSize:"32px", fontWeight:"900", lineHeight:"1", textShadow:"0 0 20px rgba(125,211,252,0.5)" }}>
            ${discountedSqftCost.toFixed(2)}
          </div>
          <div style={{ color:"rgba(255,255,255,0.4)", fontSize:"11px", fontWeight:"600" }}>base cost</div>
        </div>
      </div>
      {discountPercent > 0 && (
        <div className="savings-pulse" style={{ marginTop:"10px", padding:"12px 20px", borderRadius:"12px", background:"linear-gradient(135deg, rgba(16,185,129,0.2), rgba(5,150,105,0.15))", border:"1.5px solid rgba(16,185,129,0.5)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
            <span style={{ fontSize:"16px" }}>✓</span>
            <div>
              <div style={{ color:"#34d399", fontSize:"13px", fontWeight:"800", letterSpacing:"0.5px" }}>
                You Save {Math.round(discountPercent*100)}%
              </div>
              <div style={{ color:"rgba(52,211,153,0.7)", fontSize:"11px", fontWeight:"600" }}>
                Volume discount applied
              </div>
            </div>
          </div>
          <div style={{ color:"#34d399", fontSize:"20px", fontWeight:"900", textShadow:"0 0 12px rgba(52,211,153,0.6)", whiteSpace:"nowrap", flexShrink:0 }}>
            -${(sqftCost * discountPercent).toFixed(2)}
          </div>
        </div>
      )}
    </div>
  );
})()}
</div>
{/* Beds (not Bedrooms) */}
<div style={{ marginBottom: "35px" }}>
<label
style={{
    display: "flex",
    alignItems: "center",
    fontSize: "13px",
    fontWeight: "800",
    color: "#06b6d4",
    marginBottom: "15px",
    gap: "8px",
    letterSpacing: "1px",
    textTransform: "uppercase",
  }}
>
<Bed size={18} color="#06b6d4" />
Beds
</label>
<div
style={{
    display: "flex",
    alignItems: "center",
    gap: "20px",
    padding: "28px",
    background: "rgba(255, 255, 255, 0.95)",
    borderRadius: "20px",
    border: "2px solid rgba(255, 255, 255, 0.3)",
  }}
>
<div style={{ fontSize: "44px" }}>🛏️</div>
<button
className="counter-btn"
onClick={() =>
  setAirbnbBeds(Math.max(0, airbnbBeds - 1))
}
style={{
    width: "52px",
    height: "52px",
    background: "white",
    border: "3px solid #0284c7",
    borderRadius: "14px",
    fontSize: "28px",
    fontWeight: "800",
    color: "#0284c7",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(2, 132, 199, 0.15)",
  }}
>
−
</button>
<div
style={{
    flex: 1,
    textAlign: "center",
    fontSize: "42px",
    fontWeight: "900",
    color: "#0c4a6e",
  }}
>
{airbnbBeds}
</div>
<button
className="counter-btn"
onClick={() => setAirbnbBeds(airbnbBeds + 1)}
style={{
    width: "52px",
    height: "52px",
    background:
    "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
    border: "none",
    borderRadius: "14px",
    fontSize: "28px",
    fontWeight: "800",
    color: "white",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 6px 16px rgba(14, 165, 233, 0.3)",
  }}
>
+
</button>
</div>
</div>
</>
)}
{/* Bathrooms - SHARED BY BOTH */}
<div style={{ marginBottom: "35px" }}>
<label
style={{
    display: "flex",
    alignItems: "center",
    fontSize: "13px",
    fontWeight: "800",
    color: "#06b6d4",
    marginBottom: "15px",
    gap: "8px",
    letterSpacing: "1px",
    textTransform: "uppercase",
  }}
>
<Bath size={18} color="#06b6d4" />
Bathrooms
</label>
<div
style={{
    display: "flex",
    alignItems: "center",
    gap: "20px",
    padding: "28px",
    background: "rgba(255, 255, 255, 0.95)",
    borderRadius: "20px",
    border: "2px solid rgba(255, 255, 255, 0.3)",
  }}
>
<div style={{ fontSize: "44px" }}>🚿</div>
<button
className="counter-btn"
onClick={() => setBathrooms(Math.max(0, bathrooms - 0.5))}
style={{
    width: "52px",
    height: "52px",
    background: "white",
    border: "3px solid #0284c7",
    borderRadius: "14px",
    fontSize: "28px",
    fontWeight: "800",
    color: "#0284c7",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 4px 12px rgba(2, 132, 199, 0.15)",
  }}
>
−
</button>
<div
style={{
    flex: 1,
    textAlign: "center",
    fontSize: "42px",
    fontWeight: "900",
    color: "#0c4a6e",
  }}
>
{bathrooms}
</div>
<button
className="counter-btn"
onClick={() => setBathrooms(bathrooms + 0.5)}
style={{
    width: "52px",
    height: "52px",
    background:
    "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
    border: "none",
    borderRadius: "14px",
    fontSize: "28px",
    fontWeight: "800",
    color: "white",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 6px 16px rgba(14, 165, 233, 0.3)",
  }}
>
+
</button>
</div>
<p
style={{
    fontSize: "13px",
    color: "rgba(255, 255, 255, 0.7)",
    marginTop: "12px",
    marginBottom: 0,
    textAlign: "center",
    fontWeight: "600",
  }}
>
💡 Click + once for half bath, twice for full
</p>
</div>
<div
style={{ display: "flex", gap: "15px", marginTop: "40px" }}
>
<button
onClick={() => {
  setStep(1);
  setTimeout(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, 100);
}}
style={{
    flex: 1,
    padding: "20px",
    background: "rgba(255, 255, 255, 0.95)",
    color: "#0284c7",
    border: "2px solid rgba(255, 255, 255, 0.3)",
    borderRadius: "16px",
    fontSize: "16px",
    fontWeight: "800",
    cursor: "pointer",
    transition: "all 0.3s ease",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  }}
>
← Back
</button>
<button
onClick={handleContinueToAddOns}
disabled={
  !address ||
  !serviceType ||
  !frequency ||
  (serviceType === "House Cleaning"
    ? !squareFeetRange
    : !airbnbSquareFeet || !airbnbLaundry)
}
style={{
    flex: 2,
    padding: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    background:
    address &&
    serviceType &&
    frequency &&
    (serviceType === "House Cleaning"
      ? squareFeetRange
      : airbnbSquareFeet && airbnbLaundry)
    ? "linear-gradient(135deg, #06b6d4 0%, #0ea5e9 100%)"
    : "rgba(255, 255, 255, 0.2)",
    color: "white",
    border: "none",
    borderRadius: "16px",
    fontSize: "18px",
    fontWeight: "800",
    cursor:
    address &&
    serviceType &&
    frequency &&
    (serviceType === "House Cleaning"
      ? squareFeetRange
      : airbnbSquareFeet && airbnbLaundry)
    ? "pointer"
    : "not-allowed",
    transition: "all 0.3s ease",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  }}
>
<span>To Add-Ons</span>
<span style={{ fontSize: "24px", marginLeft: "8px" }}>
→
</span>
</button>
</div>
</div>
)}
{/* Step 3: Add-ons and Scheduling - CONDITIONAL */}
{step === 3 && (
    <div className="fade-in-up">
    <h2
    style={{
        fontSize: "32px",
        fontWeight: "900",
        color: "white",
        margin: "0 0 40px 0",
        letterSpacing: "-0.5px",
        textTransform: "uppercase",
      }}
  >
  Customize Your Clean
  </h2>
  {/* Note for Airbnb customers */}
  {serviceType === "Airbnb Cleaning" && (
      <div
      style={{
          padding: "20px",
          background: "rgba(6, 182, 212, 0.15)",
          borderRadius: "16px",
          marginBottom: "30px",
          border: "2px solid rgba(6, 182, 212, 0.3)",
        }}
    >
    <p
    style={{
        color: "white",
        fontSize: "14px",
        margin: 0,
        fontWeight: "600",
        lineHeight: "1.6",
      }}
  >
  💡 Your base price covers kitchen and appliances, dishes,
  & linens for every turnover. The options below are for
  additional services as needed.
  </p>
  </div>
)}
{/* Add-ons Grid - CONDITIONAL BASED ON SERVICE TYPE */}
<div
className="addons-grid"
style={{
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "15px",
    marginBottom: "35px",
  }}
>
{/* House Cleaning Add-ons */}
{serviceType === "House Cleaning" && (
    <>
    <div
    onClick={() =>
      setAddOns({ ...addOns, fridge: !addOns.fridge })
    }
  className="service-card"
  style={{
      padding: "28px 20px",
      border: addOns.fridge
      ? "2px solid #0ea5e9"
      : "2px solid rgba(255, 255, 255, 0.2)",
      borderRadius: "20px",
      cursor: "pointer",
      background: addOns.fridge
      ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
      : "rgba(255, 255, 255, 0.95)",
      textAlign: "center",
    }}
>
<div style={{ fontSize: "44px", marginBottom: "12px" }}>
🧊
</div>
<div
style={{
    fontSize: "14px",
    fontWeight: "800",
    color: addOns.fridge ? "white" : "#0c4a6e",
    lineHeight: "1.3",
    letterSpacing: "0.3px",
  }}
>
Inside Fridge
</div>
</div>
<div
onClick={() =>
  setAddOns({ ...addOns, oven: !addOns.oven })
}
className="service-card"
style={{
    padding: "28px 20px",
    border: addOns.oven
    ? "2px solid #0ea5e9"
    : "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    cursor: "pointer",
    background: addOns.oven
    ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
    : "rgba(255, 255, 255, 0.95)",
    textAlign: "center",
  }}
>
<div style={{ fontSize: "44px", marginBottom: "12px" }}>
🔥
</div>
<div
style={{
    fontSize: "14px",
    fontWeight: "800",
    color: addOns.oven ? "white" : "#0c4a6e",
    lineHeight: "1.3",
    letterSpacing: "0.3px",
  }}
>
Inside Oven
</div>
</div>
<div
onClick={() =>
  setAddOns({ ...addOns, microwave: !addOns.microwave })
}
className="service-card"
style={{
    padding: "28px 20px",
    border: addOns.microwave
    ? "2px solid #0ea5e9"
    : "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    cursor: "pointer",
    background: addOns.microwave
    ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
    : "rgba(255, 255, 255, 0.95)",
    textAlign: "center",
  }}
>
<div style={{ fontSize: "44px", marginBottom: "12px" }}>
📻
</div>
<div
style={{
    fontSize: "14px",
    fontWeight: "800",
    color: addOns.microwave ? "white" : "#0c4a6e",
    lineHeight: "1.3",
    letterSpacing: "0.3px",
  }}
>
Microwave
</div>
</div>
<div
className="service-card"
style={{
    padding: "20px 15px",
    border:
    addOns.linens > 0
    ? "2px solid #0ea5e9"
    : "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    background:
    addOns.linens > 0
    ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
    : "rgba(255, 255, 255, 0.95)",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
  }}
>
<div style={{ fontSize: "36px" }}>🛏️</div>
<div
style={{
    fontSize: "13px",
    fontWeight: "800",
    color: addOns.linens > 0 ? "white" : "#0c4a6e",
    lineHeight: "1.2",
    letterSpacing: "0.3px",
  }}
>
Linens
</div>
<div
style={{
    display: "flex",
    alignItems: "center",
    gap: "10px",
  }}
>
<button
onClick={() =>
  setAddOns({
      ...addOns,
      linens: Math.max(0, addOns.linens - 1),
    })
}
className="counter-btn"
style={{
    width: "32px",
    height: "32px",
    background:
    addOns.linens > 0
    ? "rgba(255, 255, 255, 0.3)"
    : "white",
    border:
    addOns.linens > 0
    ? "none"
    : "2px solid #0284c7",
    borderRadius: "8px",
    fontSize: "18px",
    color: addOns.linens > 0 ? "white" : "#0284c7",
    cursor: "pointer",
    fontWeight: "800",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
−
</button>
<div
style={{
    fontSize: "20px",
    fontWeight: "900",
    color: addOns.linens > 0 ? "white" : "#0c4a6e",
    minWidth: "24px",
    textAlign: "center",
  }}
>
{addOns.linens}
</div>
<button
onClick={() =>
  setAddOns({
      ...addOns,
      linens: addOns.linens + 1,
    })
}
className="counter-btn"
style={{
    width: "32px",
    height: "32px",
    background:
    addOns.linens > 0
    ? "rgba(255, 255, 255, 0.3)"
    : "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
    border: "none",
    borderRadius: "8px",
    fontSize: "18px",
    color: "white",
    cursor: "pointer",
    fontWeight: "800",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
+
</button>
</div>
</div>
<div
onClick={() =>
  setAddOns({ ...addOns, dishes: !addOns.dishes })
}
className="service-card"
style={{
    padding: "28px 20px",
    border: addOns.dishes
    ? "2px solid #0ea5e9"
    : "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    cursor: "pointer",
    background: addOns.dishes
    ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
    : "rgba(255, 255, 255, 0.95)",
    textAlign: "center",
  }}
>
<div style={{ fontSize: "44px", marginBottom: "12px" }}>
🍽️
</div>
<div
style={{
    fontSize: "14px",
    fontWeight: "800",
    color: addOns.dishes ? "white" : "#0c4a6e",
    lineHeight: "1.3",
    letterSpacing: "0.3px",
  }}
>
Clean Dishes
</div>
</div>
</>
)}
{/* SHARED Add-ons for both */}
<div
className="service-card"
style={{
    padding: "20px 15px",
    border:
    addOns.deepClean > 0
    ? "2px solid #0ea5e9"
    : "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    background:
    addOns.deepClean > 0
    ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
    : "rgba(255, 255, 255, 0.95)",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
  }}
>
<div style={{ fontSize: "36px" }}>✨</div>
<div
style={{
    fontSize: "13px",
    fontWeight: "800",
    color: addOns.deepClean > 0 ? "white" : "#0c4a6e",
    lineHeight: "1.2",
    letterSpacing: "0.3px",
  }}
>
Deep Clean +1hr
</div>
<div
style={{
    display: "flex",
    alignItems: "center",
    gap: "10px",
  }}
>
<button
onClick={() =>
  setAddOns({
      ...addOns,
      deepClean: Math.max(0, addOns.deepClean - 1),
    })
}
className="counter-btn"
style={{
    width: "32px",
    height: "32px",
    background:
    addOns.deepClean > 0
    ? "rgba(255, 255, 255, 0.3)"
    : "white",
    border:
    addOns.deepClean > 0 ? "none" : "2px solid #0284c7",
    borderRadius: "8px",
    fontSize: "18px",
    color: addOns.deepClean > 0 ? "white" : "#0284c7",
    cursor: "pointer",
    fontWeight: "800",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
−
</button>
<div
style={{
    fontSize: "20px",
    fontWeight: "900",
    color: addOns.deepClean > 0 ? "white" : "#0c4a6e",
    minWidth: "24px",
    textAlign: "center",
  }}
>
{addOns.deepClean}
</div>
<button
onClick={() =>
  setAddOns({
      ...addOns,
      deepClean: addOns.deepClean + 1,
    })
}
className="counter-btn"
style={{
    width: "32px",
    height: "32px",
    background:
    addOns.deepClean > 0
    ? "rgba(255, 255, 255, 0.3)"
    : "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
    border: "none",
    borderRadius: "8px",
    fontSize: "18px",
    color: "white",
    cursor: "pointer",
    fontWeight: "800",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
+
</button>
</div>
</div>
<div
className="service-card"
style={{
    padding: "20px 15px",
    border:
    addOns.windows > 0
    ? "2px solid #0ea5e9"
    : "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    background:
    addOns.windows > 0
    ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
    : "rgba(255, 255, 255, 0.95)",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
  }}
>
<div style={{ fontSize: "36px" }}>🪟</div>
<div
style={{
    fontSize: "13px",
    fontWeight: "800",
    color: addOns.windows > 0 ? "white" : "#0c4a6e",
    lineHeight: "1.2",
    letterSpacing: "0.3px",
  }}
>
Windows
</div>
<div
style={{
    display: "flex",
    alignItems: "center",
    gap: "10px",
  }}
>
<button
onClick={() =>
  setAddOns({
      ...addOns,
      windows: Math.max(0, addOns.windows - 1),
    })
}
className="counter-btn"
style={{
    width: "32px",
    height: "32px",
    background:
    addOns.windows > 0
    ? "rgba(255, 255, 255, 0.3)"
    : "white",
    border:
    addOns.windows > 0 ? "none" : "2px solid #0284c7",
    borderRadius: "8px",
    fontSize: "18px",
    color: addOns.windows > 0 ? "white" : "#0284c7",
    cursor: "pointer",
    fontWeight: "800",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
−
</button>
<div
style={{
    fontSize: "20px",
    fontWeight: "900",
    color: addOns.windows > 0 ? "white" : "#0c4a6e",
    minWidth: "24px",
    textAlign: "center",
  }}
>
{addOns.windows}
</div>
<button
onClick={() =>
  setAddOns({ ...addOns, windows: addOns.windows + 1 })
}
className="counter-btn"
style={{
    width: "32px",
    height: "32px",
    background:
    addOns.windows > 0
    ? "rgba(255, 255, 255, 0.3)"
    : "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
    border: "none",
    borderRadius: "8px",
    fontSize: "18px",
    color: "white",
    cursor: "pointer",
    fontWeight: "800",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
+
</button>
</div>
</div>
<div
className="service-card"
style={{
    padding: "20px 15px",
    border:
    addOns.pets > 0
    ? "2px solid #0ea5e9"
    : "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    background:
    addOns.pets > 0
    ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
    : "rgba(255, 255, 255, 0.95)",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
  }}
>
<div style={{ fontSize: "36px" }}>🐾</div>
<div
style={{
    fontSize: "13px",
    fontWeight: "800",
    color: addOns.pets > 0 ? "white" : "#0c4a6e",
    lineHeight: "1.2",
    letterSpacing: "0.3px",
  }}
>
Pets
</div>
<div
style={{
    display: "flex",
    alignItems: "center",
    gap: "10px",
  }}
>
<button
onClick={() =>
  setAddOns({
      ...addOns,
      pets: Math.max(0, addOns.pets - 1),
    })
}
className="counter-btn"
style={{
    width: "32px",
    height: "32px",
    background:
    addOns.pets > 0
    ? "rgba(255, 255, 255, 0.3)"
    : "white",
    border:
    addOns.pets > 0 ? "none" : "2px solid #0284c7",
    borderRadius: "8px",
    fontSize: "18px",
    color: addOns.pets > 0 ? "white" : "#0284c7",
    cursor: "pointer",
    fontWeight: "800",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
−
</button>
<div
style={{
    fontSize: "20px",
    fontWeight: "900",
    color: addOns.pets > 0 ? "white" : "#0c4a6e",
    minWidth: "24px",
    textAlign: "center",
  }}
>
{addOns.pets}
</div>
<button
onClick={() =>
  setAddOns({ ...addOns, pets: addOns.pets + 1 })
}
className="counter-btn"
style={{
    width: "32px",
    height: "32px",
    background:
    addOns.pets > 0
    ? "rgba(255, 255, 255, 0.3)"
    : "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
    border: "none",
    borderRadius: "8px",
    fontSize: "18px",
    color: "white",
    cursor: "pointer",
    fontWeight: "800",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
+
</button>
</div>
</div>
<div
className="service-card"
style={{
    padding: "20px",
    border:
    addOns.baseTrimFeet > 0
    ? "2px solid #0ea5e9"
    : "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    background:
    addOns.baseTrimFeet > 0
    ? "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
    : "rgba(255, 255, 255, 0.95)",
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "10px",
  }}
>
<div style={{ fontSize: "36px" }}>📏</div>
<div
style={{
    fontSize: "13px",
    fontWeight: "800",
    color: addOns.baseTrimFeet > 0 ? "white" : "#0c4a6e",
    lineHeight: "1.2",
    letterSpacing: "0.3px",
  }}
>
Base Trim
</div>
<input
type="number"
value={addOns.baseTrimFeet || ""}
onChange={(e) =>
  setAddOns({
      ...addOns,
      baseTrimFeet: Math.max(
        0,
        parseFloat(e.target.value) || 0
      ),
  })
}
placeholder="Enter feet"
style={{
    width: "90px",
    padding: "8px",
    fontSize: "14px",
    border: "2px solid rgba(12, 74, 110, 0.2)",
    borderRadius: "8px",
    boxSizing: "border-box",
    background: "white",
    fontWeight: "600",
    color: "#0c4a6e",
    textAlign: "center",
  }}
/>
<div
style={{
    fontSize: "11px",
    fontWeight: "700",
    color:
    addOns.baseTrimFeet > 0
    ? "rgba(255,255,255,0.8)"
    : "#64748b",
    letterSpacing: "0.5px",
  }}
>
Linear Ft
</div>
</div>
</div>
{/* Rest of Step 3 fields... */}
<div style={{ marginBottom: "35px" }}>
<label
style={{
    display: "flex",
    alignItems: "center",
    fontSize: "13px",
    fontWeight: "800",
    color: "#06b6d4",
    marginBottom: "12px",
    gap: "8px",
    letterSpacing: "1px",
    textTransform: "uppercase",
  }}
>
<Lightbulb size={18} color="#06b6d4" />
Special Attention Areas
</label>
<textarea
value={keyAreas}
onChange={(e) => setKeyAreas(e.target.value)}
placeholder="Kitchen counters, master bathroom..."
rows={3}
style={{
    width: "100%",
    padding: "18px 22px",
    fontSize: "16px",
    border: "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "16px",
    fontFamily: "inherit",
    resize: "vertical",
    boxSizing: "border-box",
    background: "rgba(255, 255, 255, 0.95)",
    fontWeight: "500",
  }}
/>
</div>
<div style={{ marginBottom: "35px" }}>
<label
style={{
    display: "flex",
    alignItems: "center",
    fontSize: "13px",
    fontWeight: "800",
    color: "#06b6d4",
    marginBottom: "12px",
    gap: "8px",
    letterSpacing: "1px",
    textTransform: "uppercase",
  }}
>
<MessageSquare size={18} color="#06b6d4" />
Additional Requests
</label>
<textarea
value={additionalNotes}
onChange={(e) => setAdditionalNotes(e.target.value)}
placeholder="Any other details we should know..."
rows={3}
style={{
    width: "100%",
    padding: "18px 22px",
    fontSize: "16px",
    border: "2px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "16px",
    fontFamily: "inherit",
    resize: "vertical",
    boxSizing: "border-box",
    background: "rgba(255, 255, 255, 0.95)",
    fontWeight: "500",
  }}
/>
</div>
<div style={{ marginBottom: "35px" }}>
<label style={{ display:"flex", alignItems:"center", fontSize:"13px", fontWeight:"800", color:"#06b6d4", marginBottom:"15px", gap:"8px", letterSpacing:"1px", textTransform:"uppercase" }}>
  <Calendar size={18} color="#06b6d4" />
  Preferred Start Date(s)
</label>
<p style={{ fontSize:"13px", color:"rgba(255,255,255,0.6)", fontWeight:"600", marginTop:"-10px", marginBottom:"16px" }}>Select your first choice and a backup date.</p>
<style>{`
  @media (max-width: 600px) { .date-grid { grid-template-columns: 1fr !important; } }
  .csu-date-input {
    transition: border-color .15s, box-shadow .15s, transform .12s;
  }
  .csu-date-input:hover {
    border-color: rgba(6,182,212,0.55) !important;
    box-shadow: 0 6px 18px rgba(6,182,212,0.18);
  }
  .csu-date-input:focus {
    border-color: #06b6d4 !important;
    outline: none;
    box-shadow: 0 0 0 4px rgba(6,182,212,0.18), 0 8px 22px rgba(6,182,212,0.22);
  }
  .csu-date-input.has-value {
    border-color: rgba(6,182,212,0.55) !important;
  }
  /* Tint the native calendar picker icon to match the form accent. */
  .csu-date-input::-webkit-calendar-picker-indicator {
    cursor: pointer;
    filter: invert(56%) sepia(85%) saturate(490%) hue-rotate(150deg) brightness(95%) contrast(95%);
    opacity: 0.85;
  }
`}</style>
<div className="date-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"15px", maxWidth:"100%", overflow:"visible" }}>
  <div>
    <label style={{ fontSize:"12px", color:"rgba(255,255,255,0.6)", marginBottom:"8px", display:"block", fontWeight:"700" }}>First Choice</label>
    <DatePicker value={preferredDay1} onChange={setPreferredDay1} placeholder="Pick first choice" />
  </div>
  <div>
    <label style={{ fontSize:"12px", color:"rgba(255,255,255,0.6)", marginBottom:"8px", display:"block", fontWeight:"700" }}>Second Choice</label>
    <DatePicker value={preferredDay2} onChange={setPreferredDay2} placeholder="Pick backup date" />
  </div>
</div>
</div>

<div data-time-windows-section style={{ marginBottom: "40px" }}>
<label style={{ display:"flex", alignItems:"center", fontSize:"13px", fontWeight:"800", color:"#06b6d4", marginBottom:"15px", gap:"8px", letterSpacing:"1px", textTransform:"uppercase" }}>
  <Clock size={18} color="#06b6d4" />
  Preferred Service Times *
</label>
<p style={{ fontSize:"13px", color:"rgba(255,255,255,0.6)", fontWeight:"600", marginTop:"-10px", marginBottom:"16px" }}>Add one or more time windows when cleaning is welcome.</p>
{/* From / To row */}
{(() => {
  // To options = times >= From (so a 10 AM From → To list starts at 10 AM).
  const fromIdx = Math.max(0, SERVICE_TIMES.indexOf(timeFrom));
  const toOptions = SERVICE_TIMES.slice(fromIdx);
  const currentWindow = `${timeFrom} – ${timeTo}`;
  const windowAlreadyAdded = timeWindows.includes(currentWindow);
  return (
    <>
      <div style={{ display:"flex", gap:"10px", alignItems:"flex-end", marginBottom:"12px", flexWrap:"wrap" }}>
        <div style={{ flex:1, minWidth:"140px" }}>
          <label style={{ fontSize:"11px", fontWeight:"700", color:"rgba(255,255,255,0.6)", letterSpacing:"0.5px", textTransform:"uppercase", display:"block", marginBottom:"6px" }}>From</label>
          <Dropdown
            value={timeFrom}
            onChange={(v) => {
              setTimeFrom(v);
              // If the new From is later than the current To, bump To
              // forward so the window stays valid.
              const newFromIdx = SERVICE_TIMES.indexOf(v);
              const curToIdx = SERVICE_TIMES.indexOf(timeTo);
              if (newFromIdx > curToIdx) setTimeTo(v);
            }}
            options={SERVICE_TIMES.map(t => ({ value: t, label: t }))}
          />
        </div>
        <div style={{ paddingBottom:"14px", color:"rgba(255,255,255,0.7)", fontWeight:"800", fontSize:"14px" }}>to</div>
        <div style={{ flex:1, minWidth:"140px" }}>
          <label style={{ fontSize:"11px", fontWeight:"700", color:"rgba(255,255,255,0.6)", letterSpacing:"0.5px", textTransform:"uppercase", display:"block", marginBottom:"6px" }}>To</label>
          <Dropdown
            value={timeTo}
            onChange={setTimeTo}
            options={toOptions.map(t => ({ value: t, label: t }))}
          />
        </div>
        <div>
          <button
            onClick={() => {
              if (!windowAlreadyAdded) setTimeWindows([...timeWindows, currentWindow]);
            }}
            disabled={windowAlreadyAdded}
            style={{
              padding:"15px 24px",
              borderRadius:"14px",
              border:"2px solid rgba(16,185,129,0.6)",
              background: windowAlreadyAdded
                ? "rgba(16,185,129,0.25)"
                : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              color:"white",
              fontSize:"14px",
              fontWeight:"900",
              cursor: windowAlreadyAdded ? "default" : "pointer",
              whiteSpace:"nowrap",
              boxShadow: windowAlreadyAdded ? "none" : "0 6px 18px rgba(16,185,129,0.35)",
              letterSpacing:"0.5px",
              textTransform:"uppercase",
              transition:"transform .12s, box-shadow .15s",
              opacity: windowAlreadyAdded ? 0.7 : 1,
            }}
          >
            {windowAlreadyAdded ? "✓ Added" : "+ Add this window"}
          </button>
        </div>
      </div>
      {/* Helper hint — the Add step trips up some customers who change
          From/To and then submit without ever clicking Add. The submit
          handler also auto-falls-back to the current From/To if no
          windows were added, but this hint pushes the explicit path. */}
      {!windowAlreadyAdded && (
        <div style={{
          padding:"10px 14px",
          background:"rgba(16,185,129,0.08)",
          border:"1px dashed rgba(16,185,129,0.45)",
          borderRadius:"10px",
          marginBottom:"14px",
          fontSize:"12px",
          color:"rgba(220,250,235,0.9)",
          fontWeight:"600",
          lineHeight:"1.6",
        }}>
          💡 Tap <strong style={{ color:"#34d399" }}>+ Add this window</strong> to lock in <strong style={{ color:"#fff" }}>{currentWindow}</strong>. You can add multiple windows.
        </div>
      )}
      {/* Added windows */}
      {timeWindows.length>0&&(
        <div style={{ display:"flex", flexWrap:"wrap", gap:"8px" }}>
          {timeWindows.map((w,i)=>(
            <div key={i} style={{ display:"inline-flex", alignItems:"center", gap:"8px", padding:"10px 16px", borderRadius:"20px", background:"linear-gradient(135deg, rgba(6,182,212,0.22), rgba(14,165,233,0.18))", border:"1.5px solid rgba(93,235,241,0.5)", boxShadow:"0 4px 12px rgba(6,182,212,0.15)" }}>
              <span style={{ fontSize:"13px", fontWeight:"700", color:"white" }}>{w}</span>
              <button onClick={()=>setTimeWindows(timeWindows.filter((_,j)=>j!==i))} style={{ background:"rgba(255,255,255,0.12)", border:"none", cursor:"pointer", color:"#a5f3fc", fontSize:"14px", fontWeight:"900", lineHeight:"1", padding:"2px 7px", borderRadius:"50%" }}>×</button>
            </div>
          ))}
        </div>
      )}
    </>
  );
})()}
</div>
<div style={{ display: "flex", gap: "15px" }}>
<button
onClick={() => {
  setStep(2);
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, 100);
}}
style={{
    flex: 1,
    padding: "20px",
    background: "rgba(255, 255, 255, 0.95)",
    color: "#0284c7",
    border: "2px solid rgba(255, 255, 255, 0.3)",
    borderRadius: "16px",
    fontSize: "16px",
    fontWeight: "800",
    cursor: "pointer",
    transition: "all 0.3s ease",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  }}
>
← Back
</button>
</div>

{/* Instant Book CTA — 10% off first 5 cleans */}
{/* overflow:visible (not hidden) so the DatePicker / time-Dropdown panels
    can extend below the container. The corner-rounding that overflow:hidden
    was buying for the inner gradient backgrounds is replaced by explicit
    top/bottom borderRadius on each inner section. */}
<div style={{
  marginTop: "16px",
  border: "2px solid rgba(16,185,129,0.4)",
  borderRadius: "16px",
  overflow: "visible",
  position: "relative",
}}>
  {/* Header — always visible */}
  <div style={{
    padding: "18px 20px",
    background: "linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.12) 100%)",
    borderRadius: "14px 14px 0 0",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
      <div style={{ fontSize: "24px" }}>⚡</div>
      <div>
        <div style={{ color: "#10b981", fontWeight: "900", fontSize: "16px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          Instant Book — Save 10%
        </div>
        <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "13px", fontWeight: "600", marginTop: "2px" }}>
          Lock in 10% off your first 5 cleanings when you book now
        </div>
      </div>
    </div>
    {/* Price math display */}
    <div style={{ background: "rgba(0,0,0,0.25)", borderRadius: "10px", padding: "12px 14px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "13px" }}>
        <span style={{ color: "rgba(255,255,255,0.7)", fontWeight: "600" }}>Subtotal</span>
        <span style={{ color: "white", fontWeight: "700" }}>${calculateSubtotal().toFixed(2)}</span>
      </div>
      {getDiscount() > 0 && (
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontSize: "13px" }}>
          <span style={{ color: "#10b981", fontWeight: "600" }}>
            ✓ {frequency === "every-week" ? "Weekly (20% off)" : frequency === "bi-weekly" ? "Bi-Weekly (15% off)" : frequency === "every-3-weeks" ? "3-Week (12% off)" : frequency === "every-4-weeks" ? "4-Week (9% off)" : "Discount"}
          </span>
          <span style={{ color: "#10b981", fontWeight: "700" }}>-${getDiscount().toFixed(2)}</span>
        </div>
      )}
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "6px", marginBottom: "6px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
          <span style={{ color: "rgba(255,255,255,0.7)", fontWeight: "600" }}>After frequency discount</span>
          <span style={{ color: "white", fontWeight: "700" }}>${calculateTotal().toFixed(2)}</span>
        </div>
        {/* Show the minimum callout when the floor kicked in, matching
            the (Min. $X) treatment on the right-sidebar total. */}
        {(() => {
          const minimum =
            serviceType === "Airbnb Cleaning" && airbnbLaundry === "no"
              ? 139.99
              : 154.99;
          if (calculateSubtotal() - getDiscount() >= minimum) return null;
          return (
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "2px" }}>
              <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "11px", fontWeight: "700" }}>
                (Min. ${minimum.toFixed(2)})
              </span>
            </div>
          );
        })()}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
        <span style={{ color: "#10b981", fontWeight: "700" }}>⚡ Instant Book (10% off)</span>
        <span style={{ color: "#10b981", fontWeight: "800" }}>-${(calculateTotal() * 0.10).toFixed(2)}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "8px", paddingTop: "8px", borderTop: "2px solid rgba(16,185,129,0.4)", fontSize: "16px" }}>
        <span style={{ color: "white", fontWeight: "900" }}>Your price per visit</span>
        <span style={{ color: "#10b981", fontWeight: "900" }}>${(calculateTotal() * 0.90).toFixed(2)}</span>
      </div>
    </div>
  </div>

  {/* Date/time picker — expands when user clicks Instant Book */}
  {!showInstantBookPicker ? (
    <div style={{ padding: "14px 20px", background: "rgba(16,185,129,0.08)", borderRadius: "0 0 14px 14px" }}>
      <button
        onClick={() => setShowInstantBookPicker(true)}
        style={{
          width: "100%", padding: "16px",
          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
          color: "white", border: "none", borderRadius: "12px",
          fontSize: "17px", fontWeight: "900", cursor: "pointer",
          boxShadow: "0 10px 30px rgba(16, 185, 129, 0.4)",
          textTransform: "uppercase", letterSpacing: "0.5px",
        }}
      >
        ⚡ Instant Book & Save!
      </button>
    </div>
  ) : (
    <div style={{ padding: "18px 20px", background: "rgba(5,30,50,0.6)", borderTop: "1px solid rgba(16,185,129,0.25)", borderRadius: "0 0 14px 14px" }}>
      <div style={{ fontSize: "13px", fontWeight: "800", color: "#10b981", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "14px" }}>
        📅 Pick Your First Cleaning Date & Time
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "14px" }}>
        <div>
          <label style={{ fontSize: "11px", fontWeight: "700", color: "rgba(255,255,255,0.6)", letterSpacing: "0.5px", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
            Date *
          </label>
          <DatePicker
            value={instantBookDate}
            onChange={setInstantBookDate}
            minDate={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
            placeholder="Pick your booking date"
            accent={instantBookDate ? "#10b981" : "rgba(16,185,129,0.5)"}
          />
        </div>
        <div>
          <label style={{ fontSize: "11px", fontWeight: "700", color: "rgba(255,255,255,0.6)", letterSpacing: "0.5px", textTransform: "uppercase", display: "block", marginBottom: "6px" }}>
            Start Time *
          </label>
          <Dropdown
            value={instantBookTime}
            onChange={setInstantBookTime}
            options={SERVICE_TIMES.map(t => ({ value: t, label: t }))}
            accent="rgba(16,185,129,0.5)"
          />
        </div>
      </div>
      {frequency && frequency !== "one-time" && (
        <div style={{ padding: "10px 14px", background: "rgba(16,185,129,0.1)", borderRadius: "10px", border: "1px solid rgba(16,185,129,0.25)", marginBottom: "14px", fontSize: "12px", color: "rgba(255,255,255,0.8)", fontWeight: "600", lineHeight: "1.6" }}>
          📅 Recurring schedule will be set up based on this date at {instantBookTime}.
          {frequency === "every-week" && " Next clean: 7 days later."}
          {frequency === "bi-weekly" && " Next clean: 14 days later."}
          {frequency === "every-3-weeks" && " Next clean: 21 days later."}
          {frequency === "every-4-weeks" && " Next clean: 28 days later."}
        </div>
      )}
      {!instantBookDate && (
        <div style={{ fontSize: "12px", color: "rgba(255,100,100,0.9)", fontWeight: "700", marginBottom: "10px" }}>
          ⚠ Please select a date to continue
        </div>
      )}
      <div style={{ display: "flex", gap: "10px" }}>
        <button
          onClick={() => setShowInstantBookPicker(false)}
          style={{ flex: 1, padding: "14px", borderRadius: "12px", background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.15)", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}
        >
          ← Back
        </button>
        <button
          onClick={() => { if (!instantBookDate) return; handleSubmit('instant_book'); }}
          disabled={!instantBookDate || isSubmitting}
          style={{
            flex: 2, padding: "14px",
            background: instantBookDate ? "linear-gradient(135deg, #10b981 0%, #059669 100%)" : "rgba(255,255,255,0.1)",
            color: "white", border: "none", borderRadius: "12px",
            fontSize: "16px", fontWeight: "900",
            cursor: instantBookDate && !isSubmitting ? "pointer" : "not-allowed",
            boxShadow: instantBookDate ? "0 10px 30px rgba(16, 185, 129, 0.4)" : "none",
            opacity: isSubmitting ? 0.8 : 1,
            textTransform: "uppercase", letterSpacing: "0.5px",
            transition: "all 0.2s ease",
          }}
        >
          {isSubmitting
            ? "Saving booking..."
            : `⚡ Continue ${instantBookDate ? `· ${new Date(instantBookDate + 'T12:00:00').toLocaleDateString('en-US', {month:'short',day:'numeric'})} @ ${instantBookTime}` : ''}`
          }
        </button>
      </div>
    </div>
  )}
</div>

{/* Request Quote button */}
<div style={{ marginTop: "12px" }}>
  <button
    onClick={() => handleSubmit('quote')}
    disabled={isSubmitting}
    style={{
      width: "100%",
      padding: "18px",
      background: "linear-gradient(135deg, #06b6d4 0%, #0ea5e9 100%)",
      color: "white",
      border: "none",
      borderRadius: "16px",
      fontSize: "17px",
      fontWeight: "800",
      cursor: isSubmitting ? "not-allowed" : "pointer",
      transition: "all 0.3s ease",
      boxShadow: "0 15px 40px rgba(6, 182, 212, 0.4)",
      letterSpacing: "0.5px",
      textTransform: "uppercase",
      opacity: isSubmitting ? 0.7 : 1,
    }}
  >
    {isSubmitting && submissionType === 'quote' ? "Sending..." : "📋 Request a Quote"}
  </button>
  <p style={{ textAlign: "center", fontSize: "12px", color: "rgba(255,255,255,0.5)", fontWeight: "600", marginTop: "8px" }}>
    We'll review your details and follow up within 24 hours
  </p>
</div>
</div>
)}
</div>
{/* Footer */}
<div
style={{
    padding: "28px 40px",
    background: "rgba(12, 74, 110, 0.8)",
    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
    textAlign: "center",
  }}
>
<p
style={{
    color: "rgba(255, 255, 255, 0.8)",
    fontSize: "14px",
    margin: "0 0 10px 0",
    fontWeight: "600",
  }}
>
Questions? Email{" "}
<a
href="mailto:AkCleaningSuCasa@gmail.com"
style={{
    color: "#06b6d4",
    fontWeight: "800",
    textDecoration: "none",
  }}
>
AkCleaningSuCasa@gmail.com
</a>
</p>
<p
style={{
    color: "rgba(255, 255, 255, 0.6)",
    fontSize: "12px",
    margin: 0,
    fontWeight: "600",
  }}
>
By booking, you agree to our{" "}
<a
href="https://img1.wsimg.com/blobby/go/a218c663-7c40-48f5-aae1-0c7e30c1037f/downloads/Terms%20and%20Conditions.pdf?ver=1721081910935"
target="_blank"
rel="noopener noreferrer"
style={{
    color: "#06b6d4",
    fontWeight: "800",
    textDecoration: "underline",
  }}
>
Terms & Conditions
</a>
</p>
</div>
</div>
{/* Price Sidebar */}
{(step === 2 || step === 3) && (
    <div
    className="fade-in-up desktop-sidebar"
    style={{
        position: "sticky",
        top: "20px",
        height: "calc(100vh - 40px)",
        display: "flex",
        flexDirection: "column",
      }}
  >
  <div
  style={{
      backgroundColor: "#020c1f",
      backgroundImage: `
        radial-gradient(circle at 20% 30%, rgba(5,53,116,0.55) 0%, transparent 45%),
        radial-gradient(circle at 80% 68%, rgba(10,79,168,0.45) 0%, transparent 40%),
        radial-gradient(circle at 55% 8%, rgba(93,235,241,0.06) 0%, transparent 30%),
        radial-gradient(ellipse at 5% 88%, rgba(5,53,116,0.4) 0%, transparent 40%),
        radial-gradient(circle 1px at center, rgba(255,255,255,0.07) 0%, transparent 100%)
      `,
      backgroundSize: "100% 100%, 100% 100%, 100% 100%, 100% 100%, 26px 26px",
      borderRadius: "28px",
      overflow: "hidden",
      boxShadow: "0 25px 70px rgba(0, 0, 0, 0.6)",
      border: "1px solid rgba(93, 235, 241, 0.2)",
      display: "flex",
      flexDirection: "column",
      height: "100%",
    }}
>
<div
style={{
    padding: "25px",
    textAlign: "center",
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    position: "relative",
    overflow: "hidden",
  }}
>
{/* Decorative rings inside sidebar header */}
<svg width="160" height="160" style={{position:"absolute",top:"-40px",right:"-30px",opacity:0.2,pointerEvents:"none"}} viewBox="0 0 160 160">
  <circle cx="80" cy="80" r="72" fill="none" stroke="rgba(93,235,241,0.8)" strokeWidth="1.2"/>
  <circle cx="80" cy="80" r="52" fill="none" stroke="rgba(125,211,252,0.5)" strokeWidth="0.8"/>
  <circle cx="80" cy="80" r="34" fill="none" stroke="rgba(93,235,241,0.4)" strokeWidth="0.6"/>
</svg>
<div style={{position:"absolute",width:"4px",height:"4px",borderRadius:"50%",background:"rgba(125,211,252,0.7)",top:"18%",right:"18%",boxShadow:"0 0 5px rgba(93,235,241,0.6)",pointerEvents:"none"}}/>
<div style={{position:"absolute",width:"3px",height:"3px",borderRadius:"50%",background:"rgba(93,235,241,0.6)",top:"65%",right:"8%",boxShadow:"0 0 4px rgba(93,235,241,0.5)",pointerEvents:"none"}}/>
<div style={{position:"absolute",width:"5px",height:"5px",borderRadius:"50%",background:"rgba(125,211,252,0.5)",bottom:"15%",left:"8%",boxShadow:"0 0 5px rgba(93,235,241,0.4)",pointerEvents:"none"}}/>
<div style={{position:"relative",zIndex:1}}>
<div style={{ fontFamily:"'Oswald', sans-serif", fontSize:"22px", fontWeight:"300", letterSpacing:"5px", textTransform:"uppercase", background:"linear-gradient(180deg, #E0F7FF 0%, #7dd3fc 25%, #0369a1 50%, #7dd3fc 75%, #E0F7FF 100%)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text", marginBottom:"8px" }}>Price Breakdown</div>
<div style={{ fontSize:"14px", color:"rgba(125,211,252,0.85)", fontWeight:"700", background:"rgba(255,255,255,0.1)", padding:"4px 12px", borderRadius:"6px", display:"inline-block" }}>Per Visit</div>
</div>
</div>
<div
className="price-breakdown-scroll"
style={{
    padding: "25px",
    overflowY: "auto",
    flex: 1,
    minHeight: 0,
  }}
>
{getPriceBreakdown().length === 0 ? (
    <div
    style={{
        textAlign: "center",
        padding: "30px 20px",
        color: "rgba(255, 255, 255, 0.5)",
        fontSize: "14px",
        fontWeight: "600",
      }}
  >
  Select services to see pricing
  </div>
) : (
<>
<div style={{ marginBottom: "20px" }}>
{getPriceBreakdown().map((item, index) => (
      <div
      key={index}
      style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 0",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
          fontSize: "14px",
        }}
    >
    <div
    style={{
        color: "rgba(255, 255, 255, 0.85)",
        fontWeight: "600",
        fontSize: "14px",
      }}
  >
  {item.label}
  </div>
  <div style={{ color: "white", fontWeight: "800", fontSize: "14px" }}>
  ${item.amount.toFixed(2)}
  </div>
  </div>
))}
</div>
<div
style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 0",
    borderTop: "2px solid rgba(93,235,241,0.3)",
    fontSize: "15px",
  }}
>
<div
style={{
    color: "#7dd3fc",
    fontWeight: "800",
    textTransform: "uppercase",
    fontSize: "14px",
  }}
>
Subtotal
</div>
<div style={{ color: "white", fontWeight: "900", fontSize: "16px" }}>
${calculateSubtotal().toFixed(2)}
</div>
</div>
{getDiscount() > 0 && (
    <div
    style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "14px 16px",
        background: "linear-gradient(135deg, rgba(16,185,129,0.15), rgba(5,150,105,0.12))",
        border: "1px solid rgba(16,185,129,0.4)",
        borderRadius: "10px",
        marginTop: "8px",
        fontSize: "14px",
      }}
  >
  <div style={{ color: "#10b981", fontWeight: "800", textTransform: "uppercase", letterSpacing: "0.5px" }}>
  ✓ {serviceType === "House Cleaning" && (
      <>
      {frequency === "every-week" && "Weekly (20% off)"}
      {frequency === "bi-weekly" && "Bi-Weekly (15% off)"}
      {frequency === "every-3-weeks" && "3-Week (12% off)"}
      {frequency === "every-4-weeks" && "4-Week (9% off)"}
      </>
    )}
{serviceType === "Airbnb Cleaning" && "Discounts Applied"}
</div>
<div style={{ color: "#10b981", fontWeight: "900", fontSize: "16px" }}>
-${getDiscount().toFixed(2)}
</div>
</div>
)}
</>
)}
</div>

{/* Price Disclaimer */}
<div style={{
  padding: "12px 20px",
  background: "rgba(255, 255, 255, 0.1)",
  borderTop: "1px solid rgba(255, 255, 255, 0.1)",
  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
}}>
  <p style={{
    color: "rgba(255, 255, 255, 0.8)",
    fontSize: "11px",
    margin: 0,
    fontWeight: "600",
    lineHeight: "1.4",
    textAlign: "center",
    fontStyle: "italic",
  }}>
    💡 This is an estimate. Final prices may vary based on property condition and specific requirements.
  </p>
</div>

<div
style={{
    padding: "25px",
    background: "linear-gradient(180deg, rgba(14,165,233,0.2) 0%, rgba(3,105,161,0.25) 100%)",
    borderTop: "1px solid rgba(93, 235, 241, 0.3)",
  }}
>
<div
style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  }}
>
<div
style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  }}
>
<div style={{ color:"white", fontWeight:"900", fontSize:"14px", letterSpacing:"1.5px", textTransform:"uppercase", marginBottom:"4px" }}>Total</div>
<div style={{ fontSize:"14px", color:"rgba(125,211,252,0.8)", fontWeight:"700", background:"rgba(255,255,255,0.1)", padding:"3px 10px", borderRadius:"6px" }}>per visit</div>
{(() => {
      const minimum =
      serviceType === "Airbnb Cleaning" &&
      airbnbLaundry === "no"
      ? 139.99
      : 154.99;
      return (
        calculateSubtotal() - getDiscount() < minimum && (
          <div style={{ color:"rgba(255,255,255,0.6)", fontSize:"11px", fontWeight:"700", marginTop:"4px" }}>
          (Min. ${minimum.toFixed(2)})
          </div>
        )
      );
})()}
</div>
<div style={{ color:"white", fontWeight:"900", fontSize:"36px", lineHeight:"1" }}>
${calculateTotal().toFixed(2)}
</div>
</div>
</div>
<div
className="satisfaction-badge"
style={{
    marginTop: "20px",
    padding: "20px",
    background: "white",
    borderRadius: "20px",
    textAlign: "center",
    boxShadow: "0 15px 40px rgba(0, 0, 0, 0.1)",
    border: "1px solid rgba(12, 74, 110, 0.1)",
  }}
>
<div style={{ fontSize: "36px", marginBottom: "12px" }}>🛡️</div>
<div
style={{
    fontSize: "13px",
    color: "#0c4a6e",
    fontWeight: "800",
    lineHeight: "1.6",
    letterSpacing: "0.3px",
  }}
>
100% SATISFACTION
<br />
GUARANTEED
</div>
</div>
</div>
</div>
)}
</div>

{/* MOBILE-ONLY PRICE DISPLAY - STICKY AT BOTTOM */}
{(step === 2 || step === 3) && (
  <div ref={mobileBarRef} className="mobile-price-sticky" style={{position:"fixed",bottom:0,left:0,right:0,zIndex:1000}}>
    <div style={{
      backgroundColor:"#020c1f",
      backgroundImage:`radial-gradient(circle at 20% 30%, rgba(5,53,116,0.55) 0%, transparent 45%),radial-gradient(circle at 80% 68%, rgba(10,79,168,0.45) 0%, transparent 40%)`,
      backgroundSize:"100% 100%, 100% 100%",
      borderTop:"2px solid rgba(93,235,241,0.35)",
      borderRadius:"16px 16px 0 0",
      boxShadow:"0 -8px 30px rgba(0,0,0,0.5)",
    }}>
      {/* Total row — always visible */}
      <div style={{padding:"12px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{color:"rgba(93,235,241,0.8)",fontSize:"11px",fontWeight:"700",letterSpacing:"1.5px",textTransform:"uppercase"}}>Estimate</div>
          {getDiscount()>0&&<div style={{color:"#10b981",fontSize:"11px",fontWeight:"700",marginTop:"2px"}}>✓ Saving ${getDiscount().toFixed(2)}</div>}
        </div>
        <div style={{textAlign:"right"}}>
          <div style={{color:"white",fontSize:"28px",fontWeight:"900",lineHeight:"1"}}>${calculateTotal().toFixed(2)}</div>
          <div style={{color:"rgba(93,235,241,0.6)",fontSize:"10px",fontWeight:"600"}}>per visit</div>
        </div>
      </div>
      {/* Line items — hard capped, always scrollable */}
      {getPriceBreakdown().length>0&&(
        <div style={{borderTop:"1px solid rgba(93,235,241,0.15)",overflowY:"scroll",maxHeight:"120px",padding:"6px 20px 10px",WebkitOverflowScrolling:"touch"}}>
          {getPriceBreakdown().map((item,i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"3px 0",fontSize:"12px",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
              <span style={{color:"rgba(255,255,255,0.8)",fontWeight:"600",flex:1,marginRight:"8px"}}>{item.label}</span>
              <span style={{color:"white",fontWeight:"700",flexShrink:0}}>${item.amount.toFixed(2)}</span>
            </div>
          ))}
          {getDiscount()>0&&(
            <div style={{display:"flex",justifyContent:"space-between",padding:"5px 0 0",borderTop:"1px solid rgba(16,185,129,0.4)",marginTop:"3px"}}>
              <span style={{color:"#10b981",fontSize:"12px",fontWeight:"800"}}>✓ Discount Applied</span>
              <span style={{color:"#10b981",fontSize:"12px",fontWeight:"900"}}>-${getDiscount().toFixed(2)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
)}

{/* Success Modal */}
{showSuccessModal && (
    <div
    style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "20px",
        animation: "fadeIn 0.3s ease-out",
      }}
  onClick={() => setShowSuccessModal(false)}
  >
  <div
  style={{
      background: "linear-gradient(135deg, #0c4a6e 0%, #0369a1 100%)",
      borderRadius: "32px",
      padding: "50px 40px",
      maxWidth: "500px",
      width: "100%",
      boxShadow: "0 30px 80px rgba(0, 0, 0, 0.5)",
      border: "2px solid rgba(6, 182, 212, 0.3)",
      textAlign: "center",
      position: "relative",
      animation: "slideInUp 0.4s ease-out",
    }}
onClick={(e) => e.stopPropagation()}
>
{/* Success Icon */}
<div
style={{
    width: "100px",
    height: "100px",
    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 30px",
    boxShadow: "0 20px 50px rgba(16, 185, 129, 0.4)",
    animation: "scaleIn 0.5s ease-out 0.2s backwards",
  }}
>
<div
style={{
    fontSize: "50px",
    animation: "rotateIn 0.6s ease-out 0.4s backwards",
  }}
>
✓
</div>
</div>
{/* Success Message */}
<h2
style={{
    fontSize: "32px",
    fontWeight: "900",
    color: "white",
    margin: "0 0 20px 0",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  }}
>
{submissionType === 'instant_book' ? 'Booking Confirmed! ⚡' : 'Quote Received!'}
</h2>
<p
style={{
    fontSize: "18px",
    color: "rgba(255, 255, 255, 0.9)",
    lineHeight: "1.6",
    marginBottom: "10px",
    fontWeight: "600",
  }}
>
Thank you for choosing Cleaning Su Casa!
</p>
<p
style={{
    fontSize: "16px",
    color: "rgba(255, 255, 255, 0.8)",
    lineHeight: "1.6",
    marginBottom: "10px",
    fontWeight: "500",
  }}
>
{submissionType === 'instant_book' ? (
  <>
    Your booking for{" "}
    <strong style={{ color: "#10b981", fontSize: "20px" }}>
      ${(calculateTotal() * 0.90).toFixed(2)}/visit
    </strong>{" "}
    is confirmed.
    <br />
    <strong style={{ color: "#10b981" }}>
      🎉 You're saving ${(calculateTotal() * 0.10).toFixed(2)} per visit for your first 5 cleans!
    </strong>
    {instantBookDate && (
      <>
        <br />
        <strong style={{ color: "white" }}>
          📅 First clean: {new Date(instantBookDate + 'T12:00:00').toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })} @ {instantBookTime}
        </strong>
      </>
    )}
  </>
) : (
  <>
    Your quote for{" "}
    <strong style={{ color: "#06b6d4", fontSize: "20px" }}>
      ${calculateTotal().toFixed(2)}
    </strong>{" "}
    has been submitted.
  </>
)}
<br />
We'll contact you at{" "}
<strong style={{ color: "white" }}>{email}</strong> shortly!</p>
{/* Close Button */}
<button
onClick={() => {
    setShowSuccessModal(false);
    window.location.reload();
  }}
style={{
    padding: "18px 40px",
    background: "linear-gradient(135deg, #06b6d4 0%, #0ea5e9 100%)",
    color: "white",
    border: "none",
    borderRadius: "16px",
    fontSize: "16px",
    fontWeight: "800",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 10px 30px rgba(6, 182, 212, 0.4)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  }}
onMouseEnter={(e) => {
    e.target.style.transform = "translateY(-2px)";
    e.target.style.boxShadow = "0 15px 40px rgba(6, 182, 212, 0.5)";
  }}
onMouseLeave={(e) => {
    e.target.style.transform = "translateY(0)";
    e.target.style.boxShadow = "0 10px 30px rgba(6, 182, 212, 0.4)";
  }}
>
Got It!
</button>
</div>
</div>
)}
{/* Animations */}
<style>{`
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(50px);
  }
to {
  opacity: 1;
  transform: translateY(0);
}
}
@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0);
  }
to {
  opacity: 1;
  transform: scale(1);
}
}
@keyframes rotateIn {
  from {
    opacity: 0;
    transform: rotate(-180deg);
  }
to {
  opacity: 1;
  transform: rotate(0);
}
}
`}</style>
{/* ── INSTANT-BOOK PAY MODAL ──────────────────────────────────────────
    Three phases:
      1. Tip selector + summary (no clientSecret yet)
      2. EmbeddedCheckout iframe (clientSecret set, paymentSuccess=false)
      3. Success state (paymentSuccess=true)
    Rendered through createPortal to escape any ancestor overflow:hidden. */}
{payModalOpen && typeof document !== 'undefined' && createPortal(
  (() => {
    const baseTotal = calculateTotal() * 0.90; // post-frequency + 10% instant-book
    const tipDollars = (() => {
      if (tipMode === 'none') return 0;
      if (tipMode === 'custom') return Math.max(0, parseFloat(tipCustom) || 0);
      return baseTotal * (parseInt(tipMode) / 100);
    })();
    const grandTotal = baseTotal + tipDollars;
    const fmt = (n) => `$${n.toFixed(2)}`;

    const TipChip = ({ value, label }) => {
      const active = tipMode === value;
      return (
        <button
          type="button"
          onClick={() => setTipMode(active && value !== 'none' ? 'none' : value)}
          style={{
            flex: 1, padding: '11px 6px', borderRadius: 10,
            border: `1.5px solid ${active ? '#10b981' : 'rgba(255,255,255,0.18)'}`,
            background: active ? 'rgba(16,185,129,0.18)' : 'rgba(255,255,255,0.06)',
            color: active ? '#34d399' : 'rgba(220,240,250,0.85)',
            fontSize: 13, fontWeight: 700, cursor: 'pointer',
            transition: 'all .15s',
            fontFamily: 'inherit',
          }}
        >{label}</button>
      );
    };

    const onContinue = async () => {
      if (!pendingQuoteId) return;
      setCreatingPaySession(true);
      setPayError('');
      try {
        const res = await fetch('https://cleansync-beryl.vercel.app/api/stripe/checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            quoteId: pendingQuoteId,
            clientName: `${firstName.trim()} ${lastName.trim()}`,
            clientEmail: email.trim(),
            instantBookDate,
            instantBookTime,
            embedded: true,
            tip: tipDollars,
          }),
        });
        const data = await res.json();
        if (!res.ok || !data.clientSecret) {
          throw new Error(data.error || 'Could not start checkout');
        }
        setEmbeddedPay({
          clientSecret: data.clientSecret,
          publishableKey: data.publishableKey,
          connectedAccountId: data.connectedAccountId,
          totalCharge: data.totalCharge,
        });
      } catch (e) {
        setPayError(e.message || 'Payment setup failed. Please try again.');
      } finally {
        setCreatingPaySession(false);
      }
    };

    const close = () => {
      // Only allow closing in phase 1 or 3 — don't kill an in-flight payment.
      if (creatingPaySession) return;
      setPayModalOpen(false);
      setEmbeddedPay(null);
      setPaymentSuccess(false);
    };

    const stripePromise = embeddedPay
      ? getStripe(embeddedPay.publishableKey, embeddedPay.connectedAccountId || '')
      : null;

    return (
      <div
        onClick={close}
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
          background: 'rgba(2,10,24,0.78)', backdropFilter: 'blur(12px)',
          padding: '24px 16px', overflowY: 'auto',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            width: '100%', maxWidth: 560,
            background: 'linear-gradient(180deg, #0a1a30 0%, #06122a 100%)',
            border: '1px solid rgba(93,235,241,0.25)',
            borderRadius: 22,
            boxShadow: '0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04) inset',
            overflow: 'hidden',
            marginTop: 30, marginBottom: 30,
            animation: 'csuModalIn .2s ease-out',
          }}
        >
          <style>{`
            @keyframes csuModalIn {
              from { opacity: 0; transform: translateY(-12px) scale(0.98); }
              to   { opacity: 1; transform: translateY(0) scale(1); }
            }
          `}</style>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '18px 22px',
            background: paymentSuccess
              ? 'linear-gradient(135deg, rgba(16,185,129,0.18), rgba(5,150,105,0.12))'
              : 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(6,182,212,0.08))',
            borderBottom: '1px solid rgba(93,235,241,0.18)',
          }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: '#e0f7fa', letterSpacing: '0.3px' }}>
                {paymentSuccess ? '🎉 Booking Confirmed!' : embeddedPay ? 'Complete Payment' : '⚡ Confirm Your Instant Book'}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(220,240,250,0.6)', marginTop: 3, fontWeight: 600 }}>
                {paymentSuccess ? 'Check your email for the receipt' : embeddedPay ? 'Secured by Stripe' : 'Add a tip if you\'d like'}
              </div>
            </div>
            <button
              onClick={close}
              disabled={creatingPaySession}
              style={{
                width: 34, height: 34, borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.12)',
                background: 'rgba(255,255,255,0.06)',
                color: 'rgba(220,240,250,0.7)', fontSize: 18,
                cursor: creatingPaySession ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >×</button>
          </div>

          {paymentSuccess ? (
            // ── Phase 3: success ──
            <div style={{ padding: '40px 28px', textAlign: 'center' }}>
              <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#e0f7fa', marginBottom: 8 }}>
                You're booked for {instantBookDate ? new Date(instantBookDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : 'your date'} at {instantBookTime}.
              </div>
              <div style={{ fontSize: 13, color: 'rgba(220,240,250,0.7)', fontWeight: 600, lineHeight: 1.6, marginBottom: 24 }}>
                We sent a confirmation email to <strong style={{ color: '#5eead4' }}>{email}</strong>. See you soon!
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
                {portalUrl ? (
                  <a
                    href={portalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '14px 32px', borderRadius: 12,
                      background: 'linear-gradient(135deg, #06b6d4 0%, #0284c7 100%)',
                      color: '#fff', fontSize: 14, fontWeight: 800,
                      letterSpacing: '0.4px', textTransform: 'uppercase',
                      textDecoration: 'none',
                      boxShadow: '0 10px 28px rgba(6,182,212,0.45)',
                      display: 'inline-block',
                      minWidth: 220,
                    }}
                  >Open My Portal →</a>
                ) : (
                  <div style={{
                    padding: '12px 22px', borderRadius: 12,
                    background: 'rgba(255,255,255,0.06)',
                    color: 'rgba(220,240,250,0.6)', fontSize: 12, fontWeight: 600,
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}>⏳ Generating your portal link…</div>
                )}
                <button
                  onClick={() => {
                    setPayModalOpen(false);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  style={{
                    padding: '10px 24px', borderRadius: 10,
                    background: 'transparent',
                    color: 'rgba(220,240,250,0.7)', fontSize: 12, fontWeight: 700,
                    border: '1px solid rgba(255,255,255,0.15)', cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >Close</button>
              </div>
            </div>
          ) : embeddedPay && stripePromise ? (
            // ── Phase 2: embedded checkout iframe ──
            <div style={{ background: '#fff', padding: '8px 0' }}>
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{
                  clientSecret: embeddedPay.clientSecret,
                  onComplete: () => {
                    setPaymentSuccess(true);
                    // Fire-and-forget portal-link fetch. The webhook may
                    // still be processing the payment when this runs (Stripe
                    // delivers events in a separate connection), so retry a
                    // couple times if the quote isn't flagged paid yet.
                    (async () => {
                      for (let attempt = 0; attempt < 6; attempt++) {
                        try {
                          const res = await fetch(`https://cleansync-beryl.vercel.app/api/portal/from-paid-quote/${pendingQuoteId}`, { method: 'POST' });
                          if (res.ok) {
                            const data = await res.json();
                            if (data.url) { setPortalUrl(data.url); return; }
                          }
                        } catch {}
                        await new Promise(r => setTimeout(r, 1200));
                      }
                    })();
                  },
                }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          ) : (
            // ── Phase 1: tip selector + summary ──
            <div style={{ padding: '20px 24px' }}>
              {/* Order summary */}
              <div style={{
                background: 'rgba(6,182,212,0.07)',
                border: '1px solid rgba(93,235,241,0.18)',
                borderRadius: 14, padding: '14px 18px', marginBottom: 18,
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: '#7dd3fc', letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 10 }}>
                  Order Summary
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: 'rgba(220,240,250,0.7)', fontWeight: 600 }}>Service</span>
                    <span style={{ color: '#e0f7fa', fontWeight: 700 }}>{serviceType}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: 'rgba(220,240,250,0.7)', fontWeight: 600 }}>Date</span>
                    <span style={{ color: '#e0f7fa', fontWeight: 700 }}>
                      {instantBookDate ? new Date(instantBookDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—'} @ {instantBookTime}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, paddingTop: 6, borderTop: '1px solid rgba(93,235,241,0.12)', marginTop: 4 }}>
                    <span style={{ color: 'rgba(220,240,250,0.85)', fontWeight: 700 }}>Subtotal</span>
                    <span style={{ color: '#e0f7fa', fontWeight: 800 }}>{fmt(baseTotal)}</span>
                  </div>
                </div>
              </div>

              {/* Tip selector */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: '#e0f7fa' }}>
                    Add a tip <span style={{ fontSize: 11, color: 'rgba(220,240,250,0.5)', fontWeight: 500 }}>(optional)</span>
                  </span>
                  {tipDollars > 0 && (
                    <span style={{ fontSize: 13, fontWeight: 800, color: '#34d399' }}>+ {fmt(tipDollars)}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <TipChip value="15" label="15%" />
                  <TipChip value="18" label="18%" />
                  <TipChip value="20" label="20%" />
                  <TipChip value="25" label="25%" />
                  <TipChip value="custom" label="Custom" />
                </div>
                {tipMode === 'custom' && (
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, color: 'rgba(220,240,250,0.6)' }}>$</span>
                    <input
                      type="number" step="0.01" min="0" inputMode="decimal"
                      value={tipCustom}
                      onChange={e => setTipCustom(e.target.value)}
                      placeholder="0.00"
                      style={{
                        flex: 1, padding: '10px 12px', borderRadius: 10,
                        border: '1.5px solid rgba(255,255,255,0.18)',
                        background: 'rgba(255,255,255,0.06)',
                        color: '#fff', fontSize: 14, outline: 'none',
                        fontFamily: 'inherit',
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Grand total */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(5,150,105,0.08))',
                border: '1px solid rgba(16,185,129,0.35)',
                borderRadius: 14, padding: '14px 18px', marginBottom: 18,
                display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: '#e0f7fa' }}>Total</span>
                <span style={{ fontSize: 24, fontWeight: 900, color: '#34d399' }}>{fmt(grandTotal)}</span>
              </div>

              {payError && (
                <div style={{
                  padding: '10px 14px', marginBottom: 14,
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.35)',
                  borderRadius: 10,
                  fontSize: 13, color: '#fca5a5', fontWeight: 600,
                }}>{payError}</div>
              )}

              {/* CTA */}
              <button
                onClick={onContinue}
                disabled={creatingPaySession}
                style={{
                  width: '100%', padding: '16px',
                  background: creatingPaySession
                    ? 'rgba(16,185,129,0.4)'
                    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#fff', border: 'none', borderRadius: 14,
                  fontSize: 15, fontWeight: 900, cursor: creatingPaySession ? 'wait' : 'pointer',
                  boxShadow: creatingPaySession ? 'none' : '0 10px 28px rgba(16,185,129,0.4)',
                  letterSpacing: '0.5px', textTransform: 'uppercase',
                  fontFamily: 'inherit',
                }}
              >
                {creatingPaySession ? '⏳ Loading…' : `Continue to Payment · ${fmt(grandTotal)}`}
              </button>

              <div style={{ marginTop: 12, fontSize: 11, color: 'rgba(220,240,250,0.45)', textAlign: 'center', fontWeight: 600 }}>
                Payment processed securely by Stripe. You'll be charged once and your first cleaning is locked in.
              </div>
            </div>
          )}
        </div>
      </div>
    );
  })(),
  document.body
)}
</div>
);
}
